import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Wallet, Plus, RefreshCw, Trash2, Send, ShoppingCart, TrendingDown,
  Settings, Users, DollarSign, Activity, ArrowLeftRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import TradingChart from '../components/TradingChart';
import BalanceChart from '../components/BalanceChart';
import SwapHistory from '../components/SwapHistory';
import WarmupDashboard from '../components/WarmupDashboard';
import WarmupConfiguration from '../components/WarmupConfiguration';
import TokenManagement from '../components/TokenManagement';
import WarmupHistory from '../components/WarmupHistory';
import PortfolioSummary from '../components/PortfolioSummary';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const WalletManager = () => {
  const [wallets, setWallets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [filteredGroup, setFilteredGroup] = useState(null); // Фильтр для отображения кошельков
  const [selectedWallets, setSelectedWallets] = useState([]);
  const [, forceUpdate] = useState(0); // Для принудительного обновления
  const [loading, setLoading] = useState(false);
  const [showMnemonicDialog, setShowMnemonicDialog] = useState(false);
  const [selectedWalletForMnemonic, setSelectedWalletForMnemonic] = useState(null);
  const [walletMnemonic, setWalletMnemonic] = useState('');
  
  // Generation settings
  const [generateCount, setGenerateCount] = useState(10);
  const [groupName, setGroupName] = useState('');
  
  // Distribution settings
  const [fromAddress, setFromAddress] = useState('');
  const [fromMnemonic, setFromMnemonic] = useState('');
  const [senderWallet, setSenderWallet] = useState(''); // Selected wallet from dropdown
  const [distributionAmountType, setDistributionAmountType] = useState('absolute'); // 'percentage' or 'absolute'
  const [amountPerWallet, setAmountPerWallet] = useState(1.0);
  const [distributionMinPercent, setDistributionMinPercent] = useState(10);
  const [distributionMaxPercent, setDistributionMaxPercent] = useState(20);
  const [distributionDelay, setDistributionDelay] = useState(1.0);
  const [recipientFilter, setRecipientFilter] = useState('selected'); // 'all', 'zero_balance', 'selected', 'custom'
  const [distributionComment, setDistributionComment] = useState('');
  const [intermediateHops, setIntermediateHops] = useState(0);
  
  // Custom addresses for distribution
  const [customAddressesInput, setCustomAddressesInput] = useState('');
  const [parsedCustomAddresses, setParsedCustomAddresses] = useState([]);
  const [selectedCustomAddresses, setSelectedCustomAddresses] = useState([]);
  
  // Group selection for distribution
  const [selectedDistributionGroup, setSelectedDistributionGroup] = useState('');
  
  // Trading settings
  const [tokenAddress, setTokenAddress] = useState('EQCWibYIG72ifoJlXD39iXia-frDwTvA0aeL7-pmmgvj3dwf');
  const [amountType, setAmountType] = useState('percentage'); // 'percentage' or 'absolute'
  const [minPercent, setMinPercent] = useState(10);
  const [maxPercent, setMaxPercent] = useState(20);
  const [minAmountTon, setMinAmountTon] = useState(0.5);
  const [maxAmountTon, setMaxAmountTon] = useState(1.0);
  const [minTradeDelay, setMinTradeDelay] = useState(0.0);
  const [maxTradeDelay, setMaxTradeDelay] = useState(0.0); // Default to instant mode
  const [slippage, setSlippage] = useState(5.0);
  
  // Prices and balances
  const [tonPriceUsd, setTonPriceUsd] = useState(0);
  const [jettonPriceUsd, setJettonPriceUsd] = useState(0);
  const [jettonBalances, setJettonBalances] = useState({});  // {address: {balance, symbol}}
  const [isLoadingJettonBalances, setIsLoadingJettonBalances] = useState(false);

  // Analytics state
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [trackingStatus, setTrackingStatus] = useState({ running: false, snapshot_count: 0 });
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState(24); // hours

  // Helper: Get jetton balance for wallet (handles UQ/EQ address formats)
  const getJettonBalance = (walletAddress) => {
    // Try exact match first
    if (jettonBalances[walletAddress]) {
      const bal = jettonBalances[walletAddress];
      if (!bal.symbol || bal.symbol === 'Unknown' || bal.symbol === 'Error') {
        console.log(`❌ Wallet ${truncateAddress(walletAddress, 20, 6)} has bad symbol: ${bal.symbol}`);
      }
      return bal;
    }
    
    // Try alternate format (UQ <-> EQ)
    let alternateAddress;
    if (walletAddress.startsWith('UQ')) {
      alternateAddress = 'EQ' + walletAddress.substring(2);
    } else if (walletAddress.startsWith('EQ')) {
      alternateAddress = 'UQ' + walletAddress.substring(2);
    }
    
    if (alternateAddress && jettonBalances[alternateAddress]) {
      console.log(`🔄 Found balance for ${truncateAddress(walletAddress, 20, 6)} using alternate format`);
      return jettonBalances[alternateAddress];
    }
    
    // Not found - this shouldn't happen if API returned all balances
    if (Object.keys(jettonBalances).length > 0) {
      console.log(`⚠️ NO BALANCE for ${truncateAddress(walletAddress, 20, 6)}`);
      console.log(`   Available keys (first 3):`, Object.keys(jettonBalances).slice(0, 3).map(k => truncateAddress(k, 20, 0)));
    }
    
    return null;
  };
  
  // State for batch balance updates - restore from localStorage if exists
  const [balanceUpdateProgress, setBalanceUpdateProgress] = useState(() => {
    const saved = localStorage.getItem('balanceUpdateProgress');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Only restore if it was recently (within last 10 minutes)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 10 * 60 * 1000) {
          return {
            current: parsed.current || 0,
            total: parsed.total || 0,
            updating: parsed.updating || false,
            currentWallet: parsed.currentWallet || null,
            timestamp: parsed.timestamp
          };
        }
      } catch (e) {
        console.warn('Failed to restore balance update progress:', e);
      }
    }
    return { 
      current: 0, 
      total: 0, 
      updating: false,
      currentWallet: null,
      timestamp: null
    };
  });

  const SMALL_BALANCE_UPDATE_THRESHOLD = 3; // Меньшие обновления выполняем синхронно
  
  // Binance integration
  const [binanceApiKey, setBinanceApiKey] = useState('');
  const [binanceApiSecret, setBinanceApiSecret] = useState('');
  const [binanceConfigured, setBinanceConfigured] = useState(false);
  
  // Import/Export settings
  const [importAddress, setImportAddress] = useState('');
  const [importMnemonic, setImportMnemonic] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importFileContent, setImportFileContent] = useState(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [binanceBalance, setBinanceBalance] = useState(null);
  const [binanceAmountPerWallet, setBinanceAmountPerWallet] = useState(0.5);
  const [binanceRecipientFilter, setBinanceRecipientFilter] = useState('selected');
  const [binanceSelectedGroups, setBinanceSelectedGroups] = useState([]);
  const [binanceAmountType, setBinanceAmountType] = useState('absolute'); // 'percentage' or 'absolute'
  const [binanceMinAmount, setBinanceMinAmount] = useState(0.5);
  const [binanceMaxAmount, setBinanceMaxAmount] = useState(1.0);
  const [binanceMinPercent, setBinanceMinPercent] = useState(10);
  const [binanceMaxPercent, setBinanceMaxPercent] = useState(20);
  const binanceTransferAccounts = [
    { value: 'spot', label: 'Spot' },
    { value: 'funding', label: 'Funding (Пополнения)' },
    { value: 'futures', label: 'Futures (USDT-M)' }
  ];
  const [binanceTransferFrom, setBinanceTransferFrom] = useState('spot');
  const [binanceTransferTo, setBinanceTransferTo] = useState('funding');
  const [binanceTransferAsset, setBinanceTransferAsset] = useState('TON');
  const [binanceTransferAmount, setBinanceTransferAmount] = useState(1);
  const [binanceTransferLoading, setBinanceTransferLoading] = useState(false);
  
  // Progress logs
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [operationInProgress, setOperationInProgress] = useState(false);

  useEffect(() => {
    loadGroups();
    loadWallets();
    loadPrices();
    loadBinanceCredentials();
    // Load balance tracking status and history
    loadTrackingStatus();
    loadBalanceHistory(analyticsTimeRange);
    // Refresh prices every 30 seconds (even if update is in progress)
    const priceInterval = setInterval(() => {
      loadPrices().catch(err => console.error('Price refresh error:', err));
    }, 30000);
    return () => clearInterval(priceInterval);
  }, []);
  
  // Load jetton balances when wallets change
  useEffect(() => {
    if (wallets.length > 0) {
      console.log('🔄 Wallets loaded, fetching jetton balances...');
      loadJettonBalances();
    }
  }, [wallets.length]); // Trigger when wallets are loaded
  
  // Helper function to add progress log
  const addProgressLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {
      timestamp,
      message,
      type // 'info', 'success', 'error', 'warning'
    };
    setProgressLogs(prev => [...prev, logEntry]);
  };

  const loadGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/groups`);
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const loadWallets = async (group = selectedGroup) => {
    setLoading(true);
    try {
      const params = group ? { group_name: group } : {};
      const apiEndpoint = `${API_URL}/api/wallets/list`;
      console.log('🔍 Loading wallets from:', apiEndpoint, 'params:', params);
      const response = await axios.get(apiEndpoint, { params });
      
      console.log('📥 Wallets API response:', {
        count: response.data.count,
        walletsCount: response.data.wallets?.length || 0,
        hasWallets: !!response.data.wallets,
        status: response.status,
        dataType: typeof response.data,
        walletsType: Array.isArray(response.data.wallets) ? 'array' : typeof response.data.wallets
      });
      
      // Sort wallets by TON balance in descending order (highest first)
      const wallets = response.data.wallets || [];
      const sortedWallets = wallets.sort((a, b) => {
        const balanceA = parseFloat(a.ton_balance || 0);
        const balanceB = parseFloat(b.ton_balance || 0);
        return balanceB - balanceA; // Descending order
      });
      
      console.log(`✅ Loaded ${sortedWallets.length} wallets`);
      setWallets(sortedWallets);
      
      if (sortedWallets.length === 0) {
        console.warn('⚠️ No wallets found in response');
        toast.warning('No wallets found');
      }
      return sortedWallets;
    } catch (error) {
      console.error('❌ Error loading wallets:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(`Failed to load wallets: ${error.response?.data?.detail || error.message}`);
      setWallets([]); // Clear wallets on error
      return [];
    } finally {
      setLoading(false);
    }
  };


  const loadPrices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/prices`, {
        params: { jetton_master: tokenAddress }
      });
      // Only update if we got valid prices (not 0 or null)
      if (response.data.ton_usd && response.data.ton_usd > 0) {
        setTonPriceUsd(response.data.ton_usd);
      }
      if (response.data.jetton_usd && response.data.jetton_usd > 0) {
        setJettonPriceUsd(response.data.jetton_usd);
      }
    } catch (error) {
      console.error('Failed to load prices:', error);
      // Don't reset prices to 0 on error - keep previous values
      // Prices will be retried on next interval
    }
  };

  const loadJettonBalances = async (walletList = null) => {
    // Prevent concurrent calls
    if (isLoadingJettonBalances) {
      console.log('⏸️ Already loading jetton balances, skipping...');
      return;
    }
    
    try {
      setIsLoadingJettonBalances(true);
      
      // Batch request for all wallets
      const sourceWallets = walletList || wallets;
      const addresses = sourceWallets.map(w => w.address);
      if (addresses.length === 0) {
        setIsLoadingJettonBalances(false);
        return;
      }
      
      console.log(`🔄 Loading jetton balances for ${addresses.length} wallets...`);
      
      // Use DB endpoint (fast, no API calls)
      const response = await axios.post(`${API_URL}/api/wallets/jetton-balances-from-db`, {
        addresses,
        jetton_master: tokenAddress
      });
      
      console.log('💾 Jetton balances from DB:', response.data);
      
      if (response.data.success && response.data.balances) {
        const balances = response.data.balances;
        
        // Debug: check each balance
        const withSymbol = Object.keys(balances).filter(addr => balances[addr]?.symbol).length;
        const withoutSymbol = Object.keys(balances).filter(addr => !balances[addr]?.symbol).length;
        
        console.log(`✅ Loaded ${Object.keys(balances).length} jetton balances`);
        console.log(`   - With symbol: ${withSymbol}`);
        console.log(`   - Without symbol: ${withoutSymbol}`);
        
        // Fill missing balances with default values
        const completeBalances = { ...balances };
        addresses.forEach(addr => {
          if (!completeBalances[addr]) {
            console.log(`⚠️ Missing balance for ${truncateAddress(addr, 20, 6)}, setting default`);
            completeBalances[addr] = {
              balance: 0,
              balance_readable: 0,
              symbol: 'BUYT',
              decimals: 9,
              from_db: true
            };
          }
        });
        
        setJettonBalances(completeBalances);
        console.log(`✅ Set ${Object.keys(completeBalances).length} jetton balances in state`);
      } else {
        console.error('❌ Invalid response format:', response.data);
      }
    } catch (error) {
      console.error('❌ Failed to load jetton balances:', error.message);
      // Fallback: set empty balances with BUYT symbol for all wallets
      const emptyBalances = {};
      const sourceWallets = walletList || wallets;
      sourceWallets.forEach(wallet => {
        emptyBalances[wallet.address] = { 
          balance: 0,
          balance_readable: 0, 
          symbol: 'BUYT',
          decimals: 9,
          from_db: true
        };
      });
      setJettonBalances(emptyBalances);
      console.log(`⚠️ Set ${Object.keys(emptyBalances).length} fallback balances`);
    } finally {
      setIsLoadingJettonBalances(false);
    }
  };

  // Reset stuck progress
  const resetStuckProgress = () => {
    const clearedProgress = { 
      current: 0, 
      total: 0, 
      updating: false,
      currentWallet: null,
      timestamp: null
    };
    setBalanceUpdateProgress(clearedProgress);
    localStorage.removeItem('balanceUpdateProgress');
    localStorage.removeItem('balanceUpdateTaskId');
    toast.info('Прогресс обновления сброшен');
  };

  // Check for stuck progress on mount
  useEffect(() => {
    const checkStuckProgress = () => {
      const saved = localStorage.getItem('balanceUpdateProgress');
      const savedTaskId = localStorage.getItem('balanceUpdateTaskId');
      
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // If progress is older than 30 minutes, consider it stuck
          if (parsed.timestamp && Date.now() - parsed.timestamp > 30 * 60 * 1000) {
            console.warn('Stuck progress detected, resetting...');
            resetStuckProgress();
          }
        } catch (e) {
          console.warn('Failed to check progress:', e);
          resetStuckProgress();
        }
      }
      
      if (savedTaskId) {
        // Check if task is still running
        axios.get(`${API_URL}/api/wallets/update-balances/status/${savedTaskId}`)
          .then(response => {
            const status = response.data.status;
            if (status && status.status !== 'running') {
              // Task completed or failed, clean up
              localStorage.removeItem('balanceUpdateTaskId');
              if (status.status === 'completed') {
                setBalanceUpdateProgress({
                  current: status.total || 0,
                  total: status.total || 0,
                  updating: false,
                  currentWallet: null,
                  timestamp: null
                });
              } else {
                resetStuckProgress();
              }
            }
          })
          .catch(error => {
            console.warn('Error checking task status:', error);
            // If task not found, clean up
            if (error.response?.status === 404) {
              localStorage.removeItem('balanceUpdateTaskId');
              resetStuckProgress();
            }
          });
      }
    };
    
    checkStuckProgress();
  }, []);

  // Update balances one by one with visual feedback
  const runSynchronousBalanceUpdate = async (targetAddresses) => {
    let successCount = 0;
    let errorCount = 0;

    try {
      console.log('⚠️ Выполняется синхронное обновление балансов для небольшой выборки...');

      for (let i = 0; i < targetAddresses.length; i++) {
        const address = targetAddresses[i];
        const progressUpdate = { 
          current: i + 1, 
          total: targetAddresses.length,
          updating: true,
          currentWallet: address,
          timestamp: Date.now()
        };
        setBalanceUpdateProgress(progressUpdate);
        localStorage.setItem('balanceUpdateProgress', JSON.stringify(progressUpdate));

        try {
          console.log(`🔄 Updating wallet ${i + 1}/${targetAddresses.length}: ${truncateAddress(address, 20, 6)}`);

          const response = await axios.post(`${API_URL}/api/wallets/update-balances`, {
            addresses: [address],
            jetton_master: tokenAddress,
            batch_size: 1,
            delay_seconds: 0.5,
            max_retries: 1
          });

          if (response.data.success) {
            successCount++;
            console.log(`✅ Updated wallet ${i + 1}/${targetAddresses.length}`);

            try {
              const walletResponse = await axios.get(`${API_URL}/api/wallets/list`, {
                params: { _t: Date.now() }
              });

              if (walletResponse.data && walletResponse.data.wallets) {
                const updatedWallet = walletResponse.data.wallets.find(w => w.address === address);
                if (updatedWallet) {
                  setWallets(prev => {
                    const oldWallet = prev.find(w => w.address === address);
                    const oldTonBalance = oldWallet ? (parseInt(oldWallet.ton_balance || 0) / 1e9) : 0;
                    const newTonBalance = parseInt(updatedWallet.ton_balance || 0) / 1e9;
                    const updated = prev.map(w => 
                      w.address === address ? { ...updatedWallet } : w
                    );

                    if (oldTonBalance !== newTonBalance) {
                      console.log(`✅ Wallet ${address.slice(0, 20)}... TON balance updated: ${oldTonBalance.toFixed(4)} -> ${newTonBalance.toFixed(4)}`);
                    }

                    return updated;
                  });
                } else {
                  console.warn(`⚠️ Wallet ${address.slice(0, 20)}... not found in response`);
                }
              } else {
                console.warn(`⚠️ Invalid wallet response structure:`, walletResponse.data);
              }

              const balanceResponse = await axios.post(`${API_URL}/api/wallets/jetton-balances-from-db`, {
                addresses: [address],
                jetton_master: tokenAddress
              });

              if (balanceResponse.data.success && balanceResponse.data.balances) {
                setJettonBalances(prev => ({
                  ...prev,
                  ...balanceResponse.data.balances
                }));
              }
            } catch (balanceError) {
              console.warn('Could not reload balance for wallet:', balanceError);
            }
          } else {
            errorCount++;
            console.warn(`⚠️ Failed to update wallet ${i + 1}/${targetAddresses.length}`);
          }

          if (i < targetAddresses.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          errorCount++;
          console.error(`❌ Error updating wallet ${i + 1}/${targetAddresses.length}:`, error);

          if (i < targetAddresses.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      const finalProgress = { 
        current: targetAddresses.length, 
        total: targetAddresses.length, 
        updating: false,
        currentWallet: null,
        timestamp: Date.now()
      };
      setBalanceUpdateProgress(finalProgress);
      localStorage.setItem('balanceUpdateProgress', JSON.stringify(finalProgress));

      console.log('🔄 Reloading all wallets after balance update...');
      
      const timestamp = Date.now();
      const walletsResponse = await axios.get(`${API_URL}/api/wallets/list`, { 
        params: { _t: timestamp } 
      });
      
      if (walletsResponse.data.wallets) {
        const sortedWallets = walletsResponse.data.wallets.sort((a, b) => {
          const balanceA = parseFloat(a.ton_balance || 0);
          const balanceB = parseFloat(b.ton_balance || 0);
          return balanceB - balanceA;
        });
        console.log(`✅ Loaded ${sortedWallets.length} wallets with fresh data`);
        console.log(`📊 Sample wallet TON balance:`, sortedWallets[0] ? (parseInt(sortedWallets[0].ton_balance || 0) / 1e9).toFixed(4) + ' TON' : 'N/A');
        setWallets(sortedWallets);
      }
      
      await loadWallets();
      await loadJettonBalances();
      await loadPrices();
      await loadTrackingStatus();
      await loadBalanceHistory(analyticsTimeRange);

      if (successCount > 0) {
        toast.success(`Обновлено кошельков: ${successCount}/${targetAddresses.length}`);
      }
      
      if (errorCount > 0) {
        toast.warning(`Ошибок при обновлении: ${errorCount}`);
      }

    } catch (error) {
      console.error('❌ Update failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update balances');
    } finally {
      const clearedProgress = { 
        current: 0, 
        total: 0, 
        updating: false,
        currentWallet: null,
        timestamp: null
      };
      setBalanceUpdateProgress(clearedProgress);
      localStorage.removeItem('balanceUpdateProgress');
    }
  };

  const updateBalancesFromAPI = async () => {
    // Check if progress is stuck (older than 5 minutes)
    if (balanceUpdateProgress.updating && balanceUpdateProgress.timestamp) {
      const age = Date.now() - balanceUpdateProgress.timestamp;
      if (age > 5 * 60 * 1000) {
        console.warn('Progress appears stuck, resetting...');
        resetStuckProgress();
      } else {
        toast.info('Обновление балансов уже выполняется. Если зависло, нажмите "Сбросить"');
        return;
      }
    }
    
    if (balanceUpdateProgress.updating) {
      toast.info('Обновление балансов уже выполняется');
      return;
    }

    const targetAddresses = selectedWallets.length > 0
      ? selectedWallets
      : wallets.map(w => w.address);

    if (targetAddresses.length === 0) {
      toast.warning('Нет кошельков для обновления');
      return;
    }

    try {
      const initialProgress = { 
        current: 0, 
        total: targetAddresses.length, 
        updating: true,
        currentWallet: null,
        timestamp: Date.now()
      };
      setBalanceUpdateProgress(initialProgress);
      localStorage.setItem('balanceUpdateProgress', JSON.stringify(initialProgress));
      
      if (targetAddresses.length <= SMALL_BALANCE_UPDATE_THRESHOLD) {
        console.log('⚡ Небольшая выборка кошельков — используем синхронный путь');
        await runSynchronousBalanceUpdate(targetAddresses);
        return;
      }

      console.log(`🔄 Updating ${targetAddresses.length} wallet balances one by one...`);
      console.log(`🔄 Starting background balance update for ${targetAddresses.length} wallets...`);
      
      const response = await axios.post(`${API_URL}/api/wallets/update-balances?background=true`, {
        addresses: targetAddresses,
        jetton_master: tokenAddress,
        batch_size: 1,
        delay_seconds: 1.0,
        max_retries: 3
      });

      if (response.data.success && response.data.task_id) {
        const taskId = response.data.task_id;
        console.log(`✅ Background update started with task_id: ${taskId}`);
        
        // Poll for status updates with timeout handling
        let pollTimeout: NodeJS.Timeout | null = null;
        let lastUpdateTime = Date.now();
        const MAX_STUCK_TIME = 5 * 60 * 1000; // 5 minutes
        
        const pollStatus = async () => {
          try {
            const statusResponse = await axios.get(`${API_URL}/api/wallets/update-balances/status/${taskId}`);
            const status = statusResponse.data.status;
            
            if (!status) {
              // Task not found, clean up
              if (pollTimeout) clearTimeout(pollTimeout);
              localStorage.removeItem('balanceUpdateTaskId');
              resetStuckProgress();
              return;
            }
            
            lastUpdateTime = Date.now();
            
            setBalanceUpdateProgress({
              current: status.current || 0,
              total: status.total || targetAddresses.length,
              updating: status.status === 'running',
              currentWallet: status.current_wallet || null,
              timestamp: Date.now()
            });
            
            // Update balances periodically
            if (status.status === 'running') {
              await loadJettonBalances();
              if ((status.current || 0) % 5 === 0) {
                await loadPrices();
              }
              pollTimeout = setTimeout(pollStatus, 2000); // Poll every 2 seconds
            } else {
              if (pollTimeout) clearTimeout(pollTimeout);
              
              setBalanceUpdateProgress({
                current: status.total || targetAddresses.length,
                total: status.total || targetAddresses.length,
                updating: false,
                currentWallet: null,
                timestamp: null
              });
              
              localStorage.removeItem('balanceUpdateTaskId');
              
              await loadWallets();
              await loadJettonBalances();
              await loadPrices();
              await loadTrackingStatus();
              await loadBalanceHistory(analyticsTimeRange);
              
              if (status.status === 'completed') {
                toast.success(`Обновлено кошельков: ${status.success_count || 0}/${status.total || targetAddresses.length}`);
              } else {
                toast.error(`Ошибка обновления: ${status.error || 'Unknown error'}`);
              }
            }
          } catch (error) {
            console.error('Error polling status:', error);
            
            if (Date.now() - lastUpdateTime > MAX_STUCK_TIME) {
              console.warn('Polling appears stuck, resetting...');
              if (pollTimeout) clearTimeout(pollTimeout);
              localStorage.removeItem('balanceUpdateTaskId');
              resetStuckProgress();
              toast.warning('Обновление зависло и было сброшено');
              return;
            }
            
            if (error.response && error.response.status === 404) {
              if (pollTimeout) clearTimeout(pollTimeout);
              localStorage.removeItem('balanceUpdateTaskId');
              resetStuckProgress();
              return;
            }
            
            pollTimeout = setTimeout(pollStatus, 5000); // Retry after 5 seconds on error
          }
        };
        
        pollStatus();
        
        const balanceUpdateInterval = setInterval(async () => {
          if (balanceUpdateProgress.updating) {
            await loadJettonBalances();
            await loadPrices();
          } else {
            clearInterval(balanceUpdateInterval);
          }
        }, 3000);
        
        return;
      }
      
      console.log('⚠️ Background update not available, using synchronous method');
      await runSynchronousBalanceUpdate(targetAddresses);
      return;
    } catch (error) {
      console.error('❌ Update failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update balances');
    } finally {
      const clearedProgress = { 
        current: 0, 
        total: 0, 
        updating: false,
        currentWallet: null,
        timestamp: null
      };
      setBalanceUpdateProgress(clearedProgress);
      localStorage.removeItem('balanceUpdateProgress');
    }
  };

  const handleUpdateBalances = () => {
    updateBalancesFromAPI();
  };

  // Wallet generation function
  const handleGenerateWallets = async () => {
    if (!groupName || groupName.trim() === '') {
      toast.error('Please enter a group name');
      return;
    }

    if (generateCount < 1 || generateCount > 1000) {
      toast.error('Number of wallets must be between 1 and 1000');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/wallets/generate`, {
        count: generateCount,
        group_name: groupName
      });

      if (response.data.success) {
        toast.success(`Generated ${response.data.wallets?.length || generateCount} wallets in group "${groupName}"`);
        
        // Clear form
        setGenerateCount(10);
        setGroupName('');
        
        // Reload wallets and groups
        await loadGroups();
        await loadWallets();
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate wallets');
    } finally {
      setLoading(false);
    }
  };

  // Balance tracking functions
  const loadTrackingStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/balance-tracking/status`);
      setTrackingStatus(response.data);
    } catch (error) {
      console.error('Failed to load tracking status:', error);
    }
  };

  const loadBalanceHistory = async (hours = 24) => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/balance-tracking/history`, {
        params: { hours }
      });
      setBalanceHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load balance history:', error);
      setBalanceHistory([]);
    }
  };

  const startTracking = async () => {
    try {
      await axios.post(`${API_URL}/api/wallets/balance-tracking/start`);
      toast.success('Balance tracking started');
      await loadTrackingStatus();
    } catch (error) {
      console.error('Failed to start tracking:', error);
      toast.error('Failed to start tracking');
    }
  };

  const stopTracking = async () => {
    try {
      await axios.post(`${API_URL}/api/wallets/balance-tracking/stop`);
      toast.success('Balance tracking stopped');
      await loadTrackingStatus();
    } catch (error) {
      console.error('Failed to stop tracking:', error);
      toast.error('Failed to stop tracking');
    }
  };

  const takeSnapshot = async () => {
    try {
      await axios.post(`${API_URL}/api/wallets/balance-tracking/snapshot`);
      toast.success('Snapshot taken');
      await loadTrackingStatus();
      await loadBalanceHistory(analyticsTimeRange);
    } catch (error) {
      console.error('Failed to take snapshot:', error);
      toast.error('Failed to take snapshot');
    }
  };

  // Parse custom addresses from textarea input
  const parseCustomAddresses = (input) => {
    if (!input || input.trim() === '') {
      setParsedCustomAddresses([]);
      setSelectedCustomAddresses([]);
      return;
    }
    
    // Split by comma, space, or newline
    const addresses = input
      .split(/[\n,\s]+/)
      .map(addr => addr.trim())
      .filter(addr => addr.length > 0);
    
    // Validate and parse addresses
    const validated = addresses.map((addr, index) => {
      // Check if address is valid TON format (UQ/EQ or 0:)
      const isValid = /^(UQ|EQ|kQ)[A-Za-z0-9_-]{46,48}$/.test(addr) || /^0:[a-fA-F0-9]{64}$/.test(addr) || /^-1:[a-fA-F0-9]{64}$/.test(addr);
      
      // Check if address is in our wallet list
      const isManaged = wallets.some(w => w.address === addr);
      
      return {
        id: `custom-${index}`,
        address: addr,
        isValid: isValid,
        isManaged: isManaged,
        walletInfo: isManaged ? wallets.find(w => w.address === addr) : null
      };
    });
    
    // Remove duplicates
    const unique = validated.reduce((acc, current) => {
      const existing = acc.find(item => item.address === current.address);
      if (!existing) {
        acc.push(current);
      }
      return acc;
    }, []);
    
    setParsedCustomAddresses(unique);
    // Auto-select all valid addresses
    setSelectedCustomAddresses(unique.filter(a => a.isValid).map(a => a.address));
  };
  
  // Handle custom addresses input change
  const handleCustomAddressesChange = (value) => {
    setCustomAddressesInput(value);
    parseCustomAddresses(value);
  };

  const handleDistributeTon = async () => {
    // Reset and show progress dialog
    setProgressLogs([]);
    setShowProgressDialog(true);
    setOperationInProgress(true);
    setLoading(true);
    
    addProgressLog('🚀 Starting TON distribution...', 'info');
    
    // Determine sender wallet
    let senderAddress = fromAddress;
    let senderMnemonic = fromMnemonic;
    
    if (senderWallet && senderWallet !== 'custom') {
      addProgressLog(`📍 Using sender wallet: ${truncateAddress(senderWallet)}`, 'info');
      // Use selected wallet from dropdown
      const selectedWalletObj = wallets.find(w => w.address === senderWallet);
      if (!selectedWalletObj) {
        addProgressLog('❌ Selected sender wallet not found', 'error');
        toast.error('Selected sender wallet not found');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      // Fetch mnemonic for selected wallet
      try {
        addProgressLog('🔑 Fetching sender wallet mnemonic...', 'info');
        const mnemonicResponse = await axios.get(`${API_URL}/api/wallets/wallet/${senderWallet}/mnemonic`);
        senderMnemonic = mnemonicResponse.data.mnemonic;
        senderAddress = senderWallet;
        addProgressLog('✅ Mnemonic fetched successfully', 'success');
      } catch (error) {
        addProgressLog('❌ Failed to fetch sender wallet mnemonic', 'error');
        toast.error('Failed to fetch sender wallet mnemonic');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
    } else if (senderWallet === 'custom') {
      addProgressLog('📍 Using custom sender wallet', 'info');
      if (!fromAddress || !fromMnemonic) {
        addProgressLog('❌ Custom wallet address or mnemonic missing', 'error');
        toast.error('Please enter from address and mnemonic');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
    } else {
      addProgressLog('❌ No sender wallet selected', 'error');
      toast.error('Please select a sender wallet');
      setLoading(false);
      setOperationInProgress(false);
      return;
    }
    
    // Determine recipient addresses based on filter
    let recipientAddresses = [];
    if (recipientFilter === 'all') {
      recipientAddresses = wallets.map(w => w.address);
      addProgressLog(`📬 Recipients: All wallets (${recipientAddresses.length})`, 'info');
    } else if (recipientFilter === 'zero_balance') {
      recipientAddresses = wallets.filter(w => parseInt(w.ton_balance || 0) === 0).map(w => w.address);
      if (recipientAddresses.length === 0) {
        addProgressLog('❌ No wallets with zero balance found', 'error');
        toast.error('No wallets with zero balance found');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      addProgressLog(`📬 Recipients: Zero-balance wallets (${recipientAddresses.length})`, 'info');
    } else if (recipientFilter === 'selected') {
      if (selectedWallets.length === 0) {
        addProgressLog('❌ No wallets selected', 'error');
        toast.error('Please select wallets to distribute to');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      recipientAddresses = selectedWallets;
      addProgressLog(`📬 Recipients: Selected wallets (${recipientAddresses.length})`, 'info');
    } else if (recipientFilter === 'custom') {
      if (selectedCustomAddresses.length === 0) {
        addProgressLog('❌ No custom addresses selected', 'error');
        toast.error('Please enter and select custom addresses to distribute to');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      recipientAddresses = selectedCustomAddresses;
      addProgressLog(`📬 Recipients: Custom addresses (${recipientAddresses.length})`, 'info');
    } else if (recipientFilter === 'group') {
      if (!selectedDistributionGroup) {
        addProgressLog('❌ No group selected', 'error');
        toast.error('Please select a group to distribute to');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      recipientAddresses = wallets.filter(w => {
        const wGroupName = w.group_name || 'No Group';
        return wGroupName === selectedDistributionGroup;
      }).map(w => w.address);
      
      if (recipientAddresses.length === 0) {
        addProgressLog(`❌ No wallets found in group "${selectedDistributionGroup}"`, 'error');
        toast.error(`No wallets found in group "${selectedDistributionGroup}"`);
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      addProgressLog(`📬 Recipients: Group "${selectedDistributionGroup}" (${recipientAddresses.length} wallets)`, 'info');
    }
    
    // Check sender balance before distribution
    addProgressLog('💰 Checking sender balance...', 'info');
    try {
      const senderWalletObj = wallets.find(w => w.address === senderAddress);
      if (senderWalletObj) {
        const senderBalance = parseFloat(senderWalletObj.ton_balance || 0);
        addProgressLog(`  Sender balance: ${senderBalance.toFixed(4)} TON`, 'info');
        
        // Calculate required amount
        let requiredAmount = 0;
        if (distributionAmountType === 'percentage') {
          // For percentage, estimate with average
          const avgPercent = (distributionMinPercent + distributionMaxPercent) / 2;
          requiredAmount = (senderBalance * avgPercent / 100);
        } else {
          requiredAmount = amountPerWallet * recipientAddresses.length;
        }
        
        // Add buffer for gas fees (0.1 TON per transaction)
        const gasBuffer = 0.1 * recipientAddresses.length;
        const totalRequired = requiredAmount + gasBuffer;
        
        addProgressLog(`  Required: ${totalRequired.toFixed(4)} TON (${requiredAmount.toFixed(4)} + ${gasBuffer.toFixed(4)} gas)`, 'info');
        
        if (senderBalance < totalRequired) {
          addProgressLog(`❌ Insufficient balance! Need ${totalRequired.toFixed(4)} TON, have ${senderBalance.toFixed(4)} TON`, 'error');
          toast.error(`Insufficient balance: need ${totalRequired.toFixed(2)} TON, have ${senderBalance.toFixed(2)} TON`);
          setLoading(false);
          setOperationInProgress(false);
          return;
        }
        
        addProgressLog(`✅ Balance check passed`, 'success');
      } else {
        addProgressLog('⚠️ Sender wallet not in managed list, skipping balance check', 'warning');
      }
    } catch (error) {
      addProgressLog(`⚠️ Could not check balance: ${error.message}`, 'warning');
    }
    
    addProgressLog(`💰 Amount type: ${distributionAmountType}`, 'info');
    if (intermediateHops > 0) {
      addProgressLog(`🔀 Anonymization: ${intermediateHops} intermediate hops`, 'info');
    }

    try {
      addProgressLog('📤 Sending distribution request to backend...', 'info');
      
      const requestPayload = {
        from_address: senderAddress,
        from_mnemonic: senderMnemonic,
        to_addresses: recipientAddresses,
        amount_type: distributionAmountType,
        delay_seconds: distributionDelay,
        comment: distributionComment,
        intermediate_hops: intermediateHops
      };
      
      // Add amount parameters based on type
      if (distributionAmountType === 'percentage') {
        requestPayload.min_percent = distributionMinPercent;
        requestPayload.max_percent = distributionMaxPercent;
        addProgressLog(`  Range: ${distributionMinPercent}% - ${distributionMaxPercent}%`, 'info');
      } else {
        requestPayload.amount_per_wallet = amountPerWallet;
        addProgressLog(`  Amount per wallet: ${amountPerWallet} TON`, 'info');
      }
      
      const response = await axios.post(`${API_URL}/api/wallets/distribute-ton`, requestPayload);
      
      const results = response.data.results;
      addProgressLog(`\n📊 Distribution completed!`, 'success');
      addProgressLog(`✅ Successful: ${results.success.length}`, 'success');
      addProgressLog(`❌ Failed: ${results.failed.length}`, results.failed.length > 0 ? 'error' : 'info');
      
      if (results.failed.length > 0) {
        addProgressLog('\n⚠️ Failed transactions:', 'warning');
        results.failed.forEach(failure => {
          addProgressLog(`  • ${truncateAddress(failure.address)} - ${failure.error}`, 'error');
        });
      }
      
      if (results.success.length > 0) {
        addProgressLog('\n✅ Successful transactions:', 'success');
        results.success.slice(0, 5).forEach(success => {
          addProgressLog(`  • ${truncateAddress(success.address)}`, 'success');
        });
        if (results.success.length > 5) {
          addProgressLog(`  • ...and ${results.success.length - 5} more`, 'success');
        }
      }
      
      toast.success(`Distribution complete: ${results.success.length} success, ${results.failed.length} failed`);
      
      addProgressLog('\n🔄 Refreshing wallet data...', 'info');
      await loadWallets();
      addProgressLog('✅ Wallet data refreshed', 'success');
      
    } catch (error) {
      addProgressLog(`\n❌ Error: ${error.response?.data?.detail || error.message}`, 'error');
      toast.error(error.response?.data?.detail || 'Failed to distribute TON');
    } finally {
      setLoading(false);
      setOperationInProgress(false);
      addProgressLog('\n✅ Operation finished', 'success');
    }
  };

  const handleMassBuy = async () => {
    if (selectedWallets.length === 0) {
      toast.error('Please select wallets');
      return;
    }
    if (!tokenAddress) {
      toast.error('Please enter token address');
      return;
    }

    // Reset and show progress dialog
    setProgressLogs([]);
    setShowProgressDialog(true);
    setOperationInProgress(true);
    setLoading(true);
    
    addProgressLog(`🚀 Starting mass buy for ${selectedWallets.length} wallet(s)...`, 'info');
    addProgressLog(`💎 Token: ${truncateAddress(tokenAddress)}`, 'info');
    addProgressLog(`💰 Amount type: ${amountType}`, 'info');
    
    try {
      addProgressLog('📤 Sending request to backend...', 'info');
      
      const response = await axios.post(`${API_URL}/api/wallets/mass-buy`, {
        addresses: selectedWallets,
        token_address: tokenAddress,
        amount_type: amountType,
        min_percent: amountType === 'percentage' ? minPercent : null,
        max_percent: amountType === 'percentage' ? maxPercent : null,
        min_amount_ton: amountType === 'absolute' ? minAmountTon : null,
        max_amount_ton: amountType === 'absolute' ? maxAmountTon : null,
        min_delay_seconds: minTradeDelay,
        max_delay_seconds: maxTradeDelay,
        slippage: slippage
      });
      
      const results = response.data.results;
      
      addProgressLog(`\n📊 Operation completed!`, 'success');
      addProgressLog(`✅ Successful: ${results.success.length}`, 'success');
      addProgressLog(`❌ Failed: ${results.failed.length}`, results.failed.length > 0 ? 'error' : 'info');
      
      if (results.failed.length > 0) {
        addProgressLog('\n⚠️ Failed transactions:', 'warning');
        results.failed.forEach(failure => {
          addProgressLog(`  • ${truncateAddress(failure.address)} - ${failure.error}`, 'error');
          toast.error(`Failed for ${truncateAddress(failure.address)}`, {
            description: failure.error
          });
        });
      }
      
      if (results.success.length > 0) {
        addProgressLog('\n✅ Successful transactions:', 'success');
        results.success.forEach(success => {
          const txHash = success.transaction_hash || 'pending';
          addProgressLog(`  • ${truncateAddress(success.address)} - TX: ${truncateAddress(txHash, 16, 8)}`, 'success');
        });
        toast.success(`Mass buy initiated for ${results.success.length} wallet(s)`);
      } else {
        toast.warning('No wallets processed successfully');
      }
      
      addProgressLog('\n🔄 Refreshing wallet data...', 'info');
      await loadWallets();
      addProgressLog('✅ Wallet data refreshed', 'success');
      
    } catch (error) {
      addProgressLog(`\n❌ Error: ${error.response?.data?.detail || error.message}`, 'error');
      toast.error('Failed to execute mass buy');
    } finally {
      setLoading(false);
      setOperationInProgress(false);
      addProgressLog('\n✅ Operation finished', 'success');
    }
  };

  const handleMassSell = async () => {
    if (selectedWallets.length === 0) {
      toast.error('Please select wallets');
      return;
    }

    console.log('🚀 Starting mass sell for wallets:', selectedWallets);
    setLoading(true);
    try {
      const payload = {
        addresses: selectedWallets,
        token_address: tokenAddress,
        min_percent: minPercent,
        max_percent: maxPercent,
        min_delay_seconds: minTradeDelay,
        max_delay_seconds: maxTradeDelay,
        slippage: slippage
      };
      
      console.log('📤 Mass sell payload:', payload);
      
      const response = await axios.post(`${API_URL}/api/wallets/mass-sell`, payload);
      
      console.log('📥 Mass sell response:', response.data);
      
      const results = response.data.results;
      
      if (results && results.success && results.success.length > 0) {
        toast.success(`✅ Mass sell complete: ${results.success.length} success, ${results.failed?.length || 0} failed`);
        console.log('✅ Sell results:', results);
      } else if (results && results.failed && results.failed.length > 0) {
        toast.error(`❌ Mass sell failed for ${results.failed.length} wallets`);
        console.error('❌ Failed wallets:', results.failed);
      } else {
        toast.info('Mass sell completed but no results returned');
        console.warn('⚠️ No results in response:', results);
      }
      
      // Reload wallets and balances
      await loadWallets();
      await loadJettonBalances();
    } catch (error) {
      console.error('❌ Mass sell error:', error);
      toast.error(`Failed to execute mass sell: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleWalletSelection = (address) => {
    setSelectedWallets(prev => 
      prev.includes(address) 
        ? prev.filter(a => a !== address)
        : [...prev, address]
    );
  };

  // Фильтрация кошельков по выбранной группе
  const filteredWallets = React.useMemo(() => {
    console.log('🟠 Фильтрация кошельков:', { filteredGroup, всегоКошельков: wallets.length });
    
    if (!filteredGroup) {
      console.log('⚪ Нет фильтра, возвращаем все кошельки');
      return wallets;
    }
    
    const filtered = wallets.filter(w => {
      const wGroupName = (w.group_name || 'No Group').trim();
      const filterGroupName = filteredGroup.trim();
      const matches = wGroupName === filterGroupName;
      return matches;
    });
    
    console.log('✅ Отфильтровано кошельков:', filtered.length, 'из', wallets.length);
    console.log('📋 Группы в отфильтрованном списке:', [...new Set(filtered.map(w => w.group_name || 'No Group'))]);
    
    return filtered;
  }, [filteredGroup, wallets]);

  const selectAllWallets = () => {
    setSelectedWallets(filteredWallets.map(w => w.address));
  };

  const deselectAllWallets = () => {
    setSelectedWallets([]);
  };

  const selectWalletsByGroup = (groupName) => {
    const groupWallets = wallets.filter(w => {
      const wGroupName = w.group_name || 'No Group';
      return wGroupName === groupName;
    }).map(w => w.address);
    
    setSelectedWallets(groupWallets);
    toast.success(`Selected ${groupWallets.length} wallets from ${groupName}`);
  };

  // Обработчик клика на карточку группы - устанавливает фильтр
  const handleGroupFilter = (groupName) => {
    console.log('🔵 handleGroupFilter вызван:', { groupName, текущийFilteredGroup: filteredGroup });
    
    if (filteredGroup === groupName) {
      // Если группа уже выбрана, сбрасываем фильтр
      console.log('🟢 Сбрасываем фильтр');
      setFilteredGroup(null);
      forceUpdate(prev => prev + 1);
      toast.info('Показаны все группы');
    } else {
      // Устанавливаем фильтр
      console.log('🟡 Устанавливаем фильтр для группы:', groupName);
      setFilteredGroup(groupName);
      forceUpdate(prev => prev + 1);
      toast.success(`Показаны кошельки группы: ${groupName}`);
    }
  };

  const getWalletGroups = () => {
    const groupsMap = {};
    wallets.forEach(wallet => {
      const groupName = wallet.group_name || 'No Group';
      if (!groupsMap[groupName]) {
        groupsMap[groupName] = {
          name: groupName,
          count: 0,
          totalTON: 0,
          totalBUYT: 0
        };
      }
      groupsMap[groupName].count++;
      groupsMap[groupName].totalTON += (parseInt(wallet.ton_balance || 0)) / 1e9;
      
      const balance = getJettonBalance(wallet.address);
      if (balance) {
        groupsMap[groupName].totalBUYT += balance.balance_readable || 0;
      }
    });
    return Object.values(groupsMap);
  };


  const formatBalance = (balance) => {
    const num = parseInt(balance || 0) / 1_000_000_000;
    return num.toFixed(4);
  };

  const formatAddress = (addr) => {
    if (!addr || typeof addr !== 'string') return 'N/A';
    return addr;
  };

  const truncateAddress = (addr, prefix = 12, suffix = 8) => {
    if (!addr || typeof addr !== 'string') return 'N/A';
    if (addr.length <= prefix + suffix + 3) return addr;
    return `${addr.substring(0, prefix)}...${addr.substring(addr.length - suffix)}`;
  };

  const copyToClipboard = (text, label = 'Address') => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleShowMnemonic = async (address) => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/wallet/${address}/mnemonic`);
      setWalletMnemonic(response.data.mnemonic);
      setSelectedWalletForMnemonic(address);
      setShowMnemonicDialog(true);
    } catch (error) {
      toast.error('Failed to load mnemonic');
    }
  };

  // Binance Integration Functions
  const loadBinanceCredentials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/binance/credentials`);
      if (response.data.configured) {
        setBinanceApiKey(response.data.api_key);
        setBinanceApiSecret(response.data.api_secret);
        setBinanceConfigured(true);
        // Load balance if configured
        loadBinanceBalance();
      }
    } catch (error) {
      console.error('Error loading Binance credentials:', error);
    }
  };

  const saveBinanceCredentials = async () => {
    if (!binanceApiKey || !binanceApiSecret) {
      toast.error('Please enter both API Key and Secret');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/binance/credentials`, {
        api_key: binanceApiKey,
        api_secret: binanceApiSecret
      });
      
      setBinanceConfigured(true);
      toast.success('Binance credentials saved successfully');
      
      // Load balance after saving
      await loadBinanceBalance();
    } catch (error) {
      console.error('Error saving Binance credentials:', error);
      toast.error(error.response?.data?.detail || 'Failed to save Binance credentials');
    } finally {
      setLoading(false);
    }
  };

  const loadBinanceBalance = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/binance/balance`);
      setBinanceBalance(response.data);
    } catch (error) {
      console.error('Error loading Binance balance:', error);
      if (error.response?.status === 400) {
        setBinanceConfigured(false);
      }
    }
  };

  const handleBinanceDistribute = async () => {
    if (!binanceConfigured) {
      toast.error('Please configure Binance credentials first');
      return;
    }

    // Determine recipient addresses
    let recipientAddresses = [];
    if (binanceRecipientFilter === 'all') {
      recipientAddresses = wallets.map(w => w.address);
    } else if (binanceRecipientFilter === 'zero_balance') {
      recipientAddresses = wallets.filter(w => parseInt(w.ton_balance || 0) === 0).map(w => w.address);
    } else if (binanceRecipientFilter === 'groups' && binanceSelectedGroups.length > 0) {
      recipientAddresses = wallets
        .filter(w => binanceSelectedGroups.includes(w.group_name || 'No Group'))
        .map(w => w.address);
    } else {
      recipientAddresses = selectedWallets;
    }

    if (recipientAddresses.length === 0) {
      toast.error('No wallets selected for distribution');
      return;
    }

    // Validate amount parameters based on type
    let amountPerWallet = null;
    let minAmount = null;
    let maxAmount = null;
    let minPercent = null;
    let maxPercent = null;

    if (binanceAmountType === 'absolute') {
      if (binanceMinAmount <= 0 || binanceMaxAmount <= 0) {
        toast.error('Amount must be greater than 0');
        return;
      }
      if (binanceMinAmount > binanceMaxAmount) {
        toast.error('Min amount must be less than or equal to max amount');
        return;
      }
      minAmount = binanceMinAmount;
      maxAmount = binanceMaxAmount;
      // Calculate average for total required estimation
      amountPerWallet = (binanceMinAmount + binanceMaxAmount) / 2;
    } else {
      if (binanceMinPercent <= 0 || binanceMaxPercent <= 0 || binanceMinPercent > 100 || binanceMaxPercent > 100) {
        toast.error('Percent must be between 0 and 100');
        return;
      }
      if (binanceMinPercent > binanceMaxPercent) {
        toast.error('Min percent must be less than or equal to max percent');
        return;
      }
      minPercent = binanceMinPercent;
      maxPercent = binanceMaxPercent;
      // For percentage mode, we need balance to calculate
      if (!binanceBalance || !binanceBalance.free) {
        toast.error('Cannot calculate percentage: balance not available');
        return;
      }
      // For percentage mode, amounts are calculated dynamically from remaining balance
      // So we don't need to check total required upfront - backend handles it dynamically
      amountPerWallet = null; // Not used for percentage mode
    }

    // Only check balance for absolute mode (percentage mode is handled dynamically on backend)
    if (binanceAmountType === 'absolute') {
      const totalRequired = amountPerWallet * recipientAddresses.length;
      if (binanceBalance && totalRequired > binanceBalance.free) {
        toast.error(`Insufficient balance. Estimated required: ${totalRequired.toFixed(2)} TON, Available: ${binanceBalance.free} TON`);
        return;
      }
    }

    setLoading(true);
    let taskId = null;
    let progressInterval = null;
    
    try {
      toast.info(`Starting distribution to ${recipientAddresses.length} wallets...`);
      
      const requestData = {
        addresses: recipientAddresses,
        amount_type: binanceAmountType,
        delay_seconds: 10.0
      };

      if (binanceAmountType === 'absolute') {
        requestData.min_amount = minAmount;
        requestData.max_amount = maxAmount;
      } else {
        requestData.min_percent = minPercent;
        requestData.max_percent = maxPercent;
      }
      
      // Start distribution (async)
      const response = await axios.post(`${API_URL}/api/binance/distribute`, requestData);
      taskId = response.data.task_id;
      
      toast.info(`Distribution started. Task ID: ${taskId.substring(0, 8)}...`, {
        description: `Processing ${recipientAddresses.length} wallets with 10-20s delays between each`
      });
      
      // Poll for status
      const checkStatus = async () => {
        try {
          const statusResponse = await axios.get(`${API_URL}/api/binance/distribute/status/${taskId}`);
          const status = statusResponse.data;
          
          const { current, total, successful, failed } = status.progress;
          const percent = total > 0 ? Math.round((current / total) * 100) : 0;
          
          // Update progress in toast or show progress dialog
          if (status.status === 'running') {
            // Show progress
            toast.loading(
              `Processing: ${current}/${total} (${percent}%) - Success: ${successful}, Failed: ${failed}`,
              { id: `binance-progress-${taskId}` }
            );
          } else if (status.status === 'completed') {
            // Clear progress toast
            toast.dismiss(`binance-progress-${taskId}`);
            
            // Clear interval
            if (progressInterval) {
              clearInterval(progressInterval);
              progressInterval = null;
            }
            
            // Show results
            if (status.failed && status.failed.length > 0) {
              // Log detailed errors to console
              console.group('❌ Failed Binance Withdrawals:');
              status.failed.forEach((failure, idx) => {
                console.error(`${idx + 1}. ${truncateAddress(failure.address, 12, 8)}:`, {
                  address: failure.address,
                  amount: failure.amount,
                  error: failure.error
                });
              });
              console.groupEnd();
              
              // Analyze common errors
              const whitelistErrors = status.failed.filter(f => 
                f.error && (f.error.toLowerCase().includes('whitelist') || 
                f.error.toLowerCase().includes('address not in addressbook'))
              );
              const balanceErrors = status.failed.filter(f => 
                f.error && f.error.toLowerCase().includes('insufficient balance')
              );
              
              let errorDescription = `Failed: ${status.failed.length}/${total}`;
              if (whitelistErrors.length > 0) {
                errorDescription += `\n⚠️ ${whitelistErrors.length} address(es) not whitelisted. Add them to Binance address book first.`;
              }
              if (balanceErrors.length > 0) {
                errorDescription += `\n⚠️ ${balanceErrors.length} withdrawal(s) failed due to insufficient balance.`;
              }
              
              toast.error(
                `Distribution completed with errors`,
                {
                  description: errorDescription,
                  duration: 15000
                }
              );
            } else {
              toast.success(
                `Distribution complete! ${status.successful?.length || 0}/${total} successful`
              );
            }
            
            // Refresh balance
            await loadBinanceBalance();
            setLoading(false);
          } else if (status.status === 'failed') {
            // Clear progress toast
            toast.dismiss(`binance-progress-${taskId}`);
            
            // Clear interval
            if (progressInterval) {
              clearInterval(progressInterval);
              progressInterval = null;
            }
            
            toast.error(`Distribution failed: ${status.error || 'Unknown error'}`);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error checking status:', error);
          // Don't stop polling on error, just log it
        }
      };
      
      // Poll every 2 seconds
      progressInterval = setInterval(checkStatus, 2000);
      // Check immediately
      await checkStatus();
      
    } catch (error) {
      // Clear interval on error
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      
      console.error('Error distributing from Binance:', error);
      toast.error(error.response?.data?.detail || 'Failed to start distribution');
      setLoading(false);
    }
  };

  const handleBinanceInternalTransfer = async () => {
    if (!binanceConfigured) {
      toast.error('Please configure Binance credentials first');
      return;
    }
    
    if (binanceTransferFrom === binanceTransferTo) {
      toast.error('Source and destination accounts must be different');
      return;
    }
    
    const normalizedAmount = parseFloat(binanceTransferAmount);
    if (!normalizedAmount || normalizedAmount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    
    if (!binanceTransferAsset.trim()) {
      toast.error('Asset symbol is required');
      return;
    }
    
    setBinanceTransferLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/binance/transfer`, {
        asset: binanceTransferAsset.trim().toUpperCase(),
        amount: normalizedAmount,
        from_account: binanceTransferFrom,
        to_account: binanceTransferTo
      });
      
      toast.success(
        `Transfer ${response.data.amount} ${response.data.asset} initiated (${response.data.transfer_id})`
      );
      await loadBinanceBalance();
    } catch (error) {
      console.error('Error executing Binance internal transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to execute internal transfer');
    } finally {
      setBinanceTransferLoading(false);
    }
  };

  const handleImportMnemonic = async () => {
    if (!importAddress || !importMnemonic) {
      toast.error('Please provide both address and mnemonic');
      return;
    }

    setImportLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/wallets/import-mnemonic`, {
        address: importAddress,
        mnemonic: importMnemonic
      });

      if (response.data.success) {
        toast.success(`Mnemonic imported for ${truncateAddress(importAddress, 10, 6)}`);
        
        // Show warning if address doesn't match
        if (!response.data.address_match) {
          toast.warning(`⚠️ Address mismatch! Provided: ${truncateAddress(importAddress, 10, 6)}, Derived: ${truncateAddress(response.data.derived_address, 10, 6)}`);
        }
        
        // Clear fields
        setImportAddress('');
        setImportMnemonic('');
        
        // Reload wallets
        await loadWallets();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import mnemonic');
    } finally {
      setImportLoading(false);
    }
  };

  const handleExportWallets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/export-all`);
      
      if (response.data.success) {
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        
        // Create download link
        const url = window.URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `wallets_backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Exported ${response.data.count} wallets to file`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export wallets');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        setImportFileContent(json);
        toast.success(`File loaded: ${json.wallets?.length || 0} wallets found`);
      } catch (error) {
        toast.error('Invalid JSON file');
        setImportFileContent(null);
      }
    };
    reader.readAsText(file);
  };

  const handleImportBackup = async () => {
    if (!importFileContent || !importFileContent.wallets) {
      toast.error('Please select a valid backup file');
      return;
    }

    setImportLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/wallets/import-backup`, {
        wallets: importFileContent.wallets,
        overwrite_existing: overwriteExisting
      });

      if (response.data.success) {
        const { imported, updated, skipped, errors } = response.data;
        
        let message = [];
        if (imported > 0) message.push(`${imported} imported`);
        if (updated > 0) message.push(`${updated} updated`);
        if (skipped > 0) message.push(`${skipped} skipped`);
        if (errors.length > 0) message.push(`${errors.length} errors`);
        
        toast.success(`Backup restored: ${message.join(', ')}`);
        
        if (errors.length > 0) {
          console.error('Import errors:', errors);
        }
        
        // Clear and reload
        setImportFileContent(null);
        await loadWallets();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import backup');
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-3 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2 md:gap-3">
            <Wallet className="h-6 w-6 md:h-8 md:w-8" />
            Wallet Manager
          </h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Manage multiple TON wallets for trading operations</p>
        </div>
        <Button onClick={() => loadWallets()} disabled={loading} size="sm" className="w-full md:w-auto">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards - Compact on Mobile */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-4">
        <Card>
          <CardContent className="p-3 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-600">Total Wallets</p>
                <p className="text-lg md:text-2xl font-bold">{wallets.length}</p>
              </div>
              <Users className="h-5 w-5 md:h-8 md:w-8 text-blue-500 self-end md:self-auto" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-600">Selected</p>
                <p className="text-lg md:text-2xl font-bold">{selectedWallets.length}</p>
              </div>
              <Activity className="h-5 w-5 md:h-8 md:w-8 text-purple-500 self-end md:self-auto" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-600">Groups</p>
                <p className="text-lg md:text-2xl font-bold">{groups.length}</p>
              </div>
              <Wallet className="h-5 w-5 md:h-8 md:w-8 text-green-500 self-end md:self-auto" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-600">Total Balance</p>
                <p className="text-base md:text-2xl font-bold">
                  {(wallets.reduce((sum, w) => sum + parseInt(w.ton_balance || 0), 0) / 1e9).toFixed(2)} TON
                </p>
                {tonPriceUsd > 0 && (
                  <p className="text-xs text-gray-500">
                    ${((wallets.reduce((sum, w) => sum + parseInt(w.ton_balance || 0), 0) / 1e9) * tonPriceUsd).toFixed(2)}
                  </p>
                )}
              </div>
              <DollarSign className="h-5 w-5 md:h-8 md:w-8 text-orange-500 self-end md:self-auto" />
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-2 md:col-span-1">
          <CardContent className="p-3 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-600">Total BUYT</p>
                <p className="text-base md:text-2xl font-bold">
                  {Object.values(jettonBalances).reduce((sum, b) => sum + (b?.balance_readable || 0), 0).toFixed(2)} BUYT
                </p>
                {jettonPriceUsd > 0 && (
                  <p className="text-xs text-gray-500">
                    ${(Object.values(jettonBalances).reduce((sum, b) => sum + (b?.balance_readable || 0), 0) * jettonPriceUsd).toFixed(2)}
                  </p>
                )}
              </div>
              <Activity className="h-5 w-5 md:h-8 md:w-8 text-blue-500 self-end md:self-auto" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="wallets" className="w-full">
        {/* Mobile: Scrollable tabs */}
        <div className="md:hidden overflow-x-auto scrollbar-hide">
          <TabsList className="inline-flex w-auto min-w-full">
            <TabsTrigger value="wallets" className="whitespace-nowrap">Wallets</TabsTrigger>
            <TabsTrigger value="analytics" className="whitespace-nowrap">Analytics</TabsTrigger>
            <TabsTrigger value="generate" className="whitespace-nowrap">Generate</TabsTrigger>
            <TabsTrigger value="distribute" className="whitespace-nowrap">Distribute</TabsTrigger>
            <TabsTrigger value="trade" className="whitespace-nowrap">Trade</TabsTrigger>
            <TabsTrigger value="warmup" className="whitespace-nowrap">Warmup</TabsTrigger>
            <TabsTrigger value="portfolio" className="whitespace-nowrap">Portfolio</TabsTrigger>
            <TabsTrigger value="binance" className="whitespace-nowrap">Binance</TabsTrigger>
            <TabsTrigger value="settings" className="whitespace-nowrap">Settings</TabsTrigger>
          </TabsList>
        </div>
        
        {/* Desktop: Grid tabs */}
        <TabsList className="hidden md:grid w-full grid-cols-9">
          <TabsTrigger value="wallets">Wallets</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="generate">Generate</TabsTrigger>
          <TabsTrigger value="distribute">Distribute TON</TabsTrigger>
          <TabsTrigger value="trade">Trade</TabsTrigger>
          <TabsTrigger value="warmup">Warmup</TabsTrigger>
          <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
          <TabsTrigger value="binance">Binance</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Wallets Tab */}
        <TabsContent value="wallets" className="mt-4 md:mt-6">
          <Card>
            <CardHeader className="p-3 md:p-6">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg md:text-xl">
                    Managed Wallets
                    {filteredGroup && (
                      <span className="ml-2 text-sm text-blue-600 font-normal">
                        (Группа: {filteredGroup})
                      </span>
                    )}
                  </CardTitle>
                  {filteredGroup && (
                    <Badge variant="secondary" className="text-xs">
                      Показано: {filteredWallets.length} из {wallets.length} кошельков
                    </Badge>
                  )}
                </div>
                
                {/* Mobile: Stacked Buttons */}
                <div className="flex md:hidden flex-col gap-2">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={selectAllWallets} className="flex-1">
                      Select All
                    </Button>
                    <Button size="sm" variant="outline" onClick={deselectAllWallets} className="flex-1">
                      Deselect All
                    </Button>
                  </div>
                  
                  {/* Group Selection Buttons - Mobile */}
                  <div className="flex gap-2 flex-wrap">
                    {getWalletGroups().map(group => {
                      const formatNumber = (num) => Math.floor(num).toLocaleString('en-US');
                      const isFiltered = filteredGroup === group.name;
                      return (
                        <button 
                          key={group.name}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleGroupFilter(group.name);
                          }}
                          className={`border-2 rounded-lg px-2 py-1 text-left flex-1 min-w-[120px] transition-all ${
                            isFiltered
                              ? 'border-blue-600 bg-blue-200 shadow-md'
                              : 'border-blue-300 bg-blue-50 hover:bg-blue-100'
                          }`}
                        >
                          <div className="font-semibold text-xs text-blue-900">{group.name}</div>
                          <div className="text-[9px] text-blue-600">
                            {group.count} | T:{formatNumber(group.totalTON)} | B:{formatNumber(group.totalBUYT)}
                          </div>
                        </button>
                      );
                    })}
                    {filteredGroup && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFilteredGroup(null);
                          toast.info('Показаны все группы');
                        }}
                        className="border-2 border-gray-400 bg-gray-100 hover:bg-gray-200 rounded-lg px-2 py-1 text-left flex-1 min-w-[120px]"
                      >
                        <div className="font-semibold text-xs text-gray-900">Показать все</div>
                        <div className="text-[9px] text-gray-600">Сбросить фильтр</div>
                      </button>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => {
                        const threshold = prompt('Выбрать кошельки с TON меньше чем (например, 0.5):', '0.5');
                        if (threshold) {
                          const thresholdValue = parseFloat(threshold);
                          const filtered = wallets.filter(w => (parseInt(w.ton_balance || 0) / 1e9) < thresholdValue);
                          setSelectedWallets(filtered.map(w => w.address));
                          toast.success(`Выбрано ${filtered.length} кошельков с TON < ${thresholdValue}`);
                        }
                      }}
                      className="flex-1"
                    >
                      TON &lt; N
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => {
                        const threshold = prompt('Выбрать кошельки с BUYT больше чем (например, 1000):', '1000');
                        if (threshold) {
                          const thresholdValue = parseFloat(threshold);
                          const filtered = wallets.filter(w => {
                            const balance = getJettonBalance(w.address);
                            return balance && balance.balance_readable > thresholdValue;
                          });
                          setSelectedWallets(filtered.map(w => w.address));
                          toast.success(`Выбрано ${filtered.length} кошельков с BUYT > ${thresholdValue}`);
                        }
                      }}
                      className="flex-1"
                    >
                      BUYT &gt; N
                    </Button>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleUpdateBalances} 
                    disabled={balanceUpdateProgress.updating || loading} 
                    className="w-full"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${balanceUpdateProgress.updating ? 'animate-spin' : ''}`} />
                    {balanceUpdateProgress.updating 
                      ? `Updating ${balanceUpdateProgress.current}/${balanceUpdateProgress.total}...` 
                      : 'Update Balances'
                    }
                  </Button>
                </div>

                {/* Desktop: Horizontal Buttons */}
                <div className="hidden md:flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={selectAllWallets}>
                    Select All
                  </Button>
                  <Button size="sm" variant="outline" onClick={deselectAllWallets}>
                    Deselect All
                  </Button>
                  
                  {/* Group Selection Buttons */}
                  {getWalletGroups().map(group => {
                    const formatNumber = (num) => Math.floor(num).toLocaleString('en-US');
                    const isFiltered = filteredGroup === group.name;
                    return (
                      <button 
                        key={group.name}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleGroupFilter(group.name);
                        }}
                        className={`border-2 rounded-lg px-3 py-1 transition-all text-left ${
                          isFiltered
                            ? 'border-blue-600 bg-blue-200 shadow-md hover:bg-blue-300'
                            : 'border-blue-300 bg-blue-50 hover:bg-blue-100'
                        }`}
                      >
                        <div className="font-semibold text-xs text-blue-900">{group.name}</div>
                        <div className="text-[10px] text-blue-600">
                          {group.count} wallets | TON: {formatNumber(group.totalTON)} | BUYT: {formatNumber(group.totalBUYT)}
                        </div>
                      </button>
                    );
                  })}
                  {filteredGroup && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setFilteredGroup(null);
                        toast.info('Показаны все группы');
                      }}
                      className="border-gray-400 bg-gray-100 hover:bg-gray-200"
                    >
                      Показать все
                    </Button>
                  )}
                  
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      const threshold = prompt('Выбрать кошельки с TON меньше чем (например, 0.5):', '0.5');
                      if (threshold) {
                        const thresholdValue = parseFloat(threshold);
                        const filtered = wallets.filter(w => (parseInt(w.ton_balance || 0) / 1e9) < thresholdValue);
                        setSelectedWallets(filtered.map(w => w.address));
                        toast.success(`Выбрано ${filtered.length} кошельков с TON < ${thresholdValue}`);
                      }
                    }}
                  >
                    TON &lt; N
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      const threshold = prompt('Выбрать кошельки с BUYT больше чем (например, 1000):', '1000');
                      if (threshold) {
                        const thresholdValue = parseFloat(threshold);
                        const filtered = wallets.filter(w => {
                          const balance = getJettonBalance(w.address);
                          return balance && balance.balance_readable > thresholdValue;
                        });
                        setSelectedWallets(filtered.map(w => w.address));
                        toast.success(`Выбрано ${filtered.length} кошельков с BUYT > ${thresholdValue}`);
                      }
                    }}
                  >
                    BUYT &gt; N
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleUpdateBalances} 
                    disabled={balanceUpdateProgress.updating || loading}
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${balanceUpdateProgress.updating ? 'animate-spin' : ''}`} />
                    {balanceUpdateProgress.updating 
                      ? `${balanceUpdateProgress.current}/${balanceUpdateProgress.total}` 
                      : 'Update Balances'
                    }
                  </Button>
                </div>
              </div>
              
              {/* Progress bar for balance updates */}
              {balanceUpdateProgress.updating && (
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-blue-700 font-medium">
                      {balanceUpdateProgress.currentWallet 
                        ? `Обновляется: ${truncateAddress(balanceUpdateProgress.currentWallet, 30, 10)}` 
                        : 'Обновление балансов...'}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-semibold">{balanceUpdateProgress.current} / {balanceUpdateProgress.total}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={resetStuckProgress}
                        className="h-6 px-2 text-xs"
                      >
                        Сбросить
                      </Button>
                    </div>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${balanceUpdateProgress.total > 0 ? (balanceUpdateProgress.current / balanceUpdateProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0 md:p-6">
              
              {/* Mobile: Compact Card View */}
              <div className="md:hidden" key={`mobile-wallets-${filteredGroup || 'all'}`}>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2 p-3">
                    {filteredWallets.map((wallet) => {
                      const jettonBalance = getJettonBalance(wallet.address);
                      const displaySymbol = (jettonBalance?.symbol === 'Unknown' || jettonBalance?.symbol === 'Error' || !jettonBalance?.symbol) 
                        ? 'BUYT' 
                        : jettonBalance?.symbol;
                      const isUpdating = balanceUpdateProgress.updating && balanceUpdateProgress.currentWallet === wallet.address;
                      
                      return (
                        <div 
                          key={wallet.address}
                          className={`border rounded-lg p-3 space-y-2 transition-all ${
                            isUpdating 
                              ? 'bg-blue-50 border-blue-300 border-2 animate-pulse' 
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          {/* Checkbox and Status */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedWallets.includes(wallet.address)}
                                onChange={() => toggleWalletSelection(wallet.address)}
                                className="rounded"
                              />
                              <Badge variant="secondary" className="text-xs">{wallet.group_name}</Badge>
                            </div>
                            <Badge variant={wallet.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                              {wallet.status}
                            </Badge>
                          </div>
                          
                          {/* Balances - Main Info */}
                          <div className="grid grid-cols-2 gap-3">
                            {/* TON Balance */}
                            <div>
                              <div className="text-xs text-gray-500">TON Balance</div>
                              <div className="font-semibold text-sm">
                                {formatBalance(wallet.ton_balance)} TON
                              </div>
                              {tonPriceUsd > 0 && (
                                <div className="text-xs text-gray-500">
                                  ${((wallet.ton_balance / 1e9) * tonPriceUsd).toFixed(2)}
                                </div>
                              )}
                            </div>
                            
                            {/* BUYT Balance */}
                            <div>
                              <div className="text-xs text-gray-500">Token Balance</div>
                              {jettonBalance ? (
                                <>
                                  <div className="font-semibold text-sm">
                                    {(jettonBalance.balance_readable || 0).toFixed(2)} {displaySymbol}
                                  </div>
                                  {jettonPriceUsd > 0 && jettonBalance.balance_readable > 0 && (
                                    <div className="text-xs text-gray-500">
                                      ${(jettonBalance.balance_readable * jettonPriceUsd).toFixed(2)}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="text-sm text-gray-400">
                                  {Object.keys(jettonBalances).length === 0 ? 'Loading...' : '0.00 BUYT'}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Address - Small and Clickable */}
                          <div 
                            className="text-xs font-mono text-gray-500 cursor-pointer hover:text-gray-700 truncate"
                            onClick={() => copyToClipboard(wallet.address)}
                            title="Click to copy full address"
                          >
                          {truncateAddress(wallet.address)}
                          </div>
                          
                          {/* Action Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleShowMnemonic(wallet.address)}
                            className="w-full text-xs"
                          >
                            Show Mnemonic
                          </Button>
                        </div>
                      );
                    })}
                    
                    {filteredWallets.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        {filteredGroup ? `No wallets in group "${filteredGroup}"` : 'No wallets yet. Generate some wallets to get started.'}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
              
              {/* Desktop: Table View */}
              <div className="hidden md:block overflow-x-auto" key={`desktop-wallets-${filteredGroup || 'all'}`}>
                <ScrollArea className="h-[500px]">
                  <Table className="min-w-[800px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Select</TableHead>
                        <TableHead className="min-w-[200px]">Address</TableHead>
                        <TableHead>Group</TableHead>
                        <TableHead>TON Balance</TableHead>
                        <TableHead>Token Balance</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                  <TableBody key={`table-body-${filteredGroup || 'all'}`}>
                    {filteredWallets.map((wallet) => {
                      const isUpdating = balanceUpdateProgress.updating && balanceUpdateProgress.currentWallet === wallet.address;
                      return (
                      <TableRow 
                        key={wallet.address}
                        className={isUpdating ? "bg-blue-50 border-blue-300 border-2 animate-pulse" : ""}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedWallets.includes(wallet.address)}
                            onChange={() => toggleWalletSelection(wallet.address)}
                            className="rounded"
                          />
                        </TableCell>
                        <TableCell 
                          className="font-mono text-xs cursor-pointer hover:bg-gray-100"
                          onClick={() => copyToClipboard(wallet.address)}
                          title="Click to copy"
                        >
                          {truncateAddress(wallet.address)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{wallet.group_name}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">
                              {formatBalance(wallet.ton_balance)} TON
                            </div>
                            {tonPriceUsd > 0 && (
                              <div className="text-xs text-gray-500">
                                ${((wallet.ton_balance / 1e9) * tonPriceUsd).toFixed(2)} USD
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const balance = getJettonBalance(wallet.address);
                            if (!balance) {
                              return (
                                <div className="text-gray-400">
                                  {Object.keys(jettonBalances).length === 0 ? 'Loading...' : '0.00 BUYT'}
                                </div>
                              );
                            }
                            
                            // Normalize symbol - replace Unknown/Error with BUYT
                            const displaySymbol = (balance.symbol === 'Unknown' || balance.symbol === 'Error' || !balance.symbol) 
                              ? 'BUYT' 
                              : balance.symbol;
                            
                            return (
                              <div className="space-y-1">
                                <div className="font-medium">
                                  {(balance.balance_readable || 0).toFixed(2)} {displaySymbol}
                                </div>
                                {jettonPriceUsd > 0 && balance.balance_readable > 0 && (
                                  <div className="text-xs text-gray-500">
                                    ${(balance.balance_readable * jettonPriceUsd).toFixed(2)} USD
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Badge variant={wallet.status === 'active' ? 'default' : 'secondary'}>
                            {wallet.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleShowMnemonic(wallet.address)}
                          >
                            Show Mnemonic
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {wallets.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No wallets yet. Generate some wallets to get started.
                  </div>
                )}
              </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-4 md:mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Balance Analytics
              </CardTitle>
              <CardDescription>
                Track your total balances over time (TON, BUYT, USD)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tracking Controls */}
              <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Tracking Status:</span>
                    <Badge variant={trackingStatus.running ? 'default' : 'secondary'}>
                      {trackingStatus.running ? 'Running' : 'Stopped'}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600">
                    {trackingStatus.snapshot_count} snapshots recorded • Updates every 5 minutes
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {!trackingStatus.running ? (
                    <Button onClick={startTracking} size="sm">
                      Start Tracking
                    </Button>
                  ) : (
                    <Button onClick={stopTracking} variant="outline" size="sm">
                      Stop Tracking
                    </Button>
                  )}
                  <Button onClick={takeSnapshot} variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Take Snapshot
                  </Button>
                  <Button 
                    onClick={() => {
                      loadBalanceHistory(analyticsTimeRange);
                      loadTrackingStatus();
                    }} 
                    variant="outline" 
                    size="sm"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh Data
                  </Button>
                </div>
              </div>

              {/* Time Range Selector */}
              <div className="flex gap-2 items-center">
                <span className="text-sm font-medium">Time Range:</span>
                <div className="flex gap-1">
                  {[6, 12, 24, 48, 168].map((hours) => (
                    <Button
                      key={hours}
                      size="sm"
                      variant={analyticsTimeRange === hours ? 'default' : 'outline'}
                      onClick={() => {
                        setAnalyticsTimeRange(hours);
                        loadBalanceHistory(hours);
                      }}
                      className="text-xs"
                    >
                      {hours >= 168 ? `${hours / 24}d` : `${hours}h`}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Current Totals */}
              {trackingStatus.latest_snapshot && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-600">Total TON</div>
                      <div className="text-2xl font-bold">
                        {trackingStatus.latest_snapshot.total_ton.toFixed(2)} TON
                      </div>
                      <div className="text-xs text-gray-500">
                        ${(trackingStatus.latest_snapshot.total_ton * trackingStatus.latest_snapshot.ton_price_usd).toFixed(2)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-600">Total BUYT</div>
                      <div className="text-2xl font-bold">
                        {trackingStatus.latest_snapshot.total_buyt.toFixed(2)} BUYT
                      </div>
                      <div className="text-xs text-gray-500">
                        ${(trackingStatus.latest_snapshot.total_buyt * trackingStatus.latest_snapshot.buyt_price_usd).toFixed(2)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-600">Total Value</div>
                      <div className="text-2xl font-bold">
                        ${trackingStatus.latest_snapshot.total_usd.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {trackingStatus.latest_snapshot.wallet_count} wallets
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Chart */}
              <div className="border rounded-lg p-4 bg-white">
                <h3 className="font-medium mb-3">Balance History</h3>
                {balanceHistory.length > 0 ? (
                  <BalanceChart data={balanceHistory} />
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Activity className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p>No balance history yet</p>
                    <p className="text-sm mt-2">
                      {trackingStatus.running 
                        ? 'Waiting for snapshots... First snapshot will appear soon.'
                        : 'Start tracking to begin collecting balance data.'}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Generate Tab */}
        <TabsContent value="generate" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Generate Wallets
              </CardTitle>
              <CardDescription>
                Create multiple TON wallets in a named group
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Number of Wallets</label>
                  <Input
                    type="number"
                    min="1"
                    max="1000"
                    value={generateCount}
                    onChange={(e) => setGenerateCount(parseInt(e.target.value))}
                    placeholder="10"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Group Name</label>
                  <Input
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="e.g., Group-1, TestWallets, etc."
                  />
                </div>
              </div>
              <Button onClick={handleGenerateWallets} disabled={loading} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Generate Wallets
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Distribute Tab */}
        <TabsContent value="distribute" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Distribute TON
              </CardTitle>
              <CardDescription>
                Send TON from sender wallet to recipient wallets
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Sender Wallet Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Sender Wallet</label>
                <select
                  value={senderWallet}
                  onChange={(e) => setSenderWallet(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                >
                  <option value="">Select sender wallet...</option>
                  {filteredWallets.map((wallet) => (
                    <option key={wallet.address} value={wallet.address}>
                      {`${truncateAddress(wallet.address, 8, 6)} - ${formatBalance(wallet.ton_balance)} TON`}
                    </option>
                  ))}
                  <option value="custom">Custom Address (Manual Input)</option>
                </select>
              </div>

              {/* Show manual input if custom is selected */}
              {senderWallet === 'custom' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">From Address</label>
                    <Input
                      value={fromAddress}
                      onChange={(e) => setFromAddress(e.target.value)}
                      placeholder="EQ..."
                      className="font-mono text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">From Mnemonic (24 words)</label>
                    <Input
                      type="password"
                      value={fromMnemonic}
                      onChange={(e) => setFromMnemonic(e.target.value)}
                      placeholder="word1 word2 word3..."
                    />
                  </div>
                </>
              )}

              {/* Amount Type Toggle */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Amount Mode</label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={distributionAmountType === 'absolute' ? 'default' : 'outline'}
                    onClick={() => setDistributionAmountType('absolute')}
                    className="flex-1"
                  >
                    Absolute (TON)
                  </Button>
                  <Button
                    type="button"
                    variant={distributionAmountType === 'percentage' ? 'default' : 'outline'}
                    onClick={() => setDistributionAmountType('percentage')}
                    className="flex-1"
                  >
                    Percentage (%)
                  </Button>
                </div>
              </div>

              {/* Conditional Amount Inputs */}
              {distributionAmountType === 'absolute' ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Amount per Wallet (TON)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={amountPerWallet}
                    onChange={(e) => setAmountPerWallet(parseFloat(e.target.value))}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Min % of Balance</label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={distributionMinPercent}
                      onChange={(e) => setDistributionMinPercent(parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Max % of Balance</label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={distributionMaxPercent}
                      onChange={(e) => setDistributionMaxPercent(parseFloat(e.target.value))}
                    />
                  </div>
                </div>
              )}

              {/* Recipient Filter Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Send To</label>
                <select
                  value={recipientFilter}
                  onChange={(e) => setRecipientFilter(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                >
                  <option value="selected">Selected Wallets ({selectedWallets.length})</option>
                  <option value="all">All Wallets ({wallets.length})</option>
                  <option value="zero_balance">Zero Balance Wallets ({wallets.filter(w => parseInt(w.ton_balance || 0) === 0).length})</option>
                  <option value="group">By Group</option>
                  <option value="custom">Custom Addresses ({selectedCustomAddresses.length})</option>
                </select>
              </div>

              {/* Custom Addresses Input */}
              {recipientFilter === 'custom' && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <label className="text-sm font-medium">Enter Addresses</label>
                  <p className="text-xs text-gray-600">
                    Enter TON addresses separated by comma, space, or new line
                  </p>
                  <textarea
                    value={customAddressesInput}
                    onChange={(e) => handleCustomAddressesChange(e.target.value)}
                    placeholder="UQDdaN5-GpkPGtQ8ojyEeYulA8gKRzL6SZ825AH-CLiu1dB_&#10;EQCXvoOojZWa6OEnoZC5-8GXiVNo4OTsFcXMc06TLgaBP-nN&#10;0:af720701bc8c59e6572451e73492f79861cddee9edac4581c6dc06efd2d969d3"
                    className="w-full px-3 py-2 border rounded-md bg-white font-mono text-xs"
                    rows="5"
                  />
                  
                  {parsedCustomAddresses.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          Parsed Addresses ({parsedCustomAddresses.length})
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedCustomAddresses(parsedCustomAddresses.filter(a => a.isValid).map(a => a.address))}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Select All Valid
                          </button>
                          <button
                            onClick={() => setSelectedCustomAddresses([])}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Deselect All
                          </button>
                        </div>
                      </div>
                      
                      <div className="max-h-64 overflow-y-auto space-y-1 border rounded-md p-2 bg-white">
                        {parsedCustomAddresses.map((addr) => (
                          <div
                            key={addr.id}
                            className={`flex items-center gap-2 p-2 rounded ${
                              !addr.isValid ? 'bg-red-50 border-red-200' : 'hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCustomAddresses.includes(addr.address)}
                              disabled={!addr.isValid}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCustomAddresses([...selectedCustomAddresses, addr.address]);
                                } else {
                                  setSelectedCustomAddresses(selectedCustomAddresses.filter(a => a !== addr.address));
                                }
                              }}
                              className="h-4 w-4"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-xs truncate" title={addr.address}>
                                {addr.address}
                              </div>
                              {addr.isManaged && addr.walletInfo && (
                                <div className="text-xs text-blue-600">
                                  Managed: {addr.walletInfo.name || 'Unnamed'} • {parseFloat(addr.walletInfo.ton_balance || 0).toFixed(2)} TON
                                </div>
                              )}
                              {!addr.isValid && (
                                <div className="text-xs text-red-600">
                                  ❌ Invalid TON address format
                                </div>
                              )}
                            </div>
                            {addr.isValid && (
                              <Badge variant={addr.isManaged ? "default" : "secondary"} className="text-xs">
                                {addr.isManaged ? "Managed" : "External"}
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {parsedCustomAddresses.filter(a => !a.isValid).length > 0 && (
                        <p className="text-xs text-red-600">
                          ⚠️ {parsedCustomAddresses.filter(a => !a.isValid).length} invalid address(es) will be skipped
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}


              {/* Group Selection */}
              {recipientFilter === 'group' && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <label className="text-sm font-medium">Select Group</label>
                  <p className="text-xs text-gray-600 mb-3">
                    Choose a wallet group to distribute TON to all wallets in that group
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {getWalletGroups().map(group => {
                      const formatNumber = (num) => Math.floor(num).toLocaleString('en-US');
                      const isSelected = selectedDistributionGroup === group.name;
                      
                      return (
                        <button 
                          key={group.name}
                          onClick={() => setSelectedDistributionGroup(group.name)}
                          className={`border-2 rounded-lg p-3 transition-all text-left ${
                            isSelected 
                              ? 'border-blue-600 bg-blue-100 shadow-md' 
                              : 'border-blue-200 bg-white hover:bg-blue-50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-semibold text-sm text-blue-900">{group.name}</div>
                              <div className="text-xs text-gray-600 mt-2 space-y-1">
                                <div>📦 Wallets: <span className="font-medium">{group.count}</span></div>
                                <div>💎 Total TON: <span className="font-medium">{formatNumber(group.totalTON)}</span></div>
                                <div>🪙 Total BUYT: <span className="font-medium">{formatNumber(group.totalBUYT)}</span></div>
                              </div>
                            </div>
                            {isSelected && (
                              <div className="ml-2">
                                <svg className="h-6 w-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {selectedDistributionGroup && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">
                        ✅ Selected: <span className="font-semibold">{selectedDistributionGroup}</span>
                        {' '}({wallets.filter(w => (w.group_name || 'No Group') === selectedDistributionGroup).length} wallets)
                      </p>
                    </div>
                  )}
                </div>
              )}


              {/* Delay */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Delay Between Transfers (seconds)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={distributionDelay}
                  onChange={(e) => setDistributionDelay(parseFloat(e.target.value))}
                />
              </div>

              {/* Transaction Comment */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Transaction Comment (Optional)</label>
                <Input
                  type="text"
                  value={distributionComment}
                  onChange={(e) => setDistributionComment(e.target.value)}
                  placeholder="Leave empty for no comment"
                />
              </div>

              {/* Intermediate Hops for Anonymization */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Anonymization Hops (0-5)
                  <span className="text-xs text-gray-500 ml-2">Route through intermediate wallets to hide trail</span>
                </label>
                <select
                  value={intermediateHops}
                  onChange={(e) => setIntermediateHops(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                >
                  <option value="0">0 - Direct transfer (no anonymization)</option>
                  <option value="1">1 - Through 1 intermediate wallet</option>
                  <option value="2">2 - Through 2 intermediate wallets</option>
                  <option value="3">3 - Through 3 intermediate wallets</option>
                  <option value="4">4 - Through 4 intermediate wallets</option>
                  <option value="5">5 - Through 5 intermediate wallets</option>
                </select>
                {intermediateHops > 0 && (
                  <p className="text-xs text-amber-600">
                    ⚠️ Each hop adds ~15-20 seconds delay. Intermediate wallets will be left with ~0 balance.
                  </p>
                )}
              </div>

              <Button 
                onClick={handleDistributeTon} 
                disabled={loading || !senderWallet} 
                className="w-full"
              >
                <Send className="h-4 w-4 mr-2" />
                Distribute TON
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trade Tab */}
        <TabsContent value="trade" className="mt-6">
          <div className="space-y-4">
            {/* Trading Chart and Swap History */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TradingChart 
                tokenAddress={tokenAddress} 
                tokenSymbol="BUYT"
              />
              <SwapHistory 
                tokenAddress={tokenAddress} 
                tokenSymbol="BUYT"
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Trading Settings</CardTitle>
                <CardDescription>
                  Configure parameters for mass buy/sell operations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Token Address</label>
                  <Input
                    value={tokenAddress}
                    onChange={(e) => setTokenAddress(e.target.value)}
                    placeholder="EQ..."
                    className="font-mono text-sm"
                  />
                </div>
                
                {/* Amount Type Toggle */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Amount Mode</label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={amountType === 'percentage' ? 'default' : 'outline'}
                      onClick={() => setAmountType('percentage')}
                      className="flex-1"
                    >
                      Percentage (%)
                    </Button>
                    <Button
                      type="button"
                      variant={amountType === 'absolute' ? 'default' : 'outline'}
                      onClick={() => setAmountType('absolute')}
                      className="flex-1"
                    >
                      Absolute (TON)
                    </Button>
                  </div>
                </div>
                
                {/* Conditional Inputs Based on Mode */}
                {amountType === 'percentage' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Min % of Balance</label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={minPercent}
                        onChange={(e) => setMinPercent(parseFloat(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Max % of Balance</label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={maxPercent}
                        onChange={(e) => setMaxPercent(parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Min Amount (TON)</label>
                      <Input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={minAmountTon}
                        onChange={(e) => setMinAmountTon(parseFloat(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Max Amount (TON)</label>
                      <Input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={maxAmountTon}
                        onChange={(e) => setMaxAmountTon(parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      Delay Between Trades (sec)
                      {minTradeDelay === 0 && maxTradeDelay === 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">⚡ Моментально</span>
                      )}
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-xs text-gray-500">От (min)</label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={minTradeDelay}
                          onChange={(e) => setMinTradeDelay(Math.max(0, parseFloat(e.target.value) || 0))}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-gray-500">До (max)</label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={maxTradeDelay}
                          onChange={(e) => setMaxTradeDelay(Math.max(0, parseFloat(e.target.value) || 0))}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      {minTradeDelay === 0 && maxTradeDelay === 0 
                        ? "Все сделки выполняются одновременно"
                        : `Случайная задержка от ${minTradeDelay} до ${maxTradeDelay} секунд для каждой сделки`}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Slippage Tolerance (%)</label>
                    <Input
                      type="number"
                      step="0.1"
                      value={slippage}
                      onChange={(e) => setSlippage(parseFloat(e.target.value))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-4">
              <Button 
                onClick={handleMassBuy} 
                disabled={loading || selectedWallets.length === 0}
                className="h-20"
              >
                <ShoppingCart className="h-6 w-6 mr-2" />
                <div className="text-left">
                  <div className="font-bold">Mass Buy Token</div>
                  <div className="text-xs opacity-80">{selectedWallets.length} wallets selected</div>
                </div>
              </Button>

              <Button 
                onClick={handleMassSell} 
                disabled={loading || selectedWallets.length === 0}
                variant="destructive"
                className="h-20"
              >
                <TrendingDown className="h-6 w-6 mr-2" />
                <div className="text-left">
                  <div className="font-bold">Mass Sell Token</div>
                  <div className="text-xs opacity-80">{selectedWallets.length} wallets selected</div>
                </div>
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Warmup Tab */}
        <TabsContent value="warmup" className="mt-6">
          <div className="space-y-4">
            <WarmupDashboard />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <WarmupConfiguration />
              <TokenManagement />
            </div>
            <WarmupHistory />
          </div>
        </TabsContent>

        {/* Portfolio Tab */}
        <TabsContent value="portfolio" className="mt-6">
          <div className="space-y-4">
            <PortfolioSummary />
            <div className="text-center text-muted-foreground p-8">
              Portfolio analytics and token positions table coming soon...
            </div>
          </div>
        </TabsContent>

        {/* Binance Tab */}
        <TabsContent value="binance" className="mt-6">
          <div className="space-y-6">
            {/* Binance Settings Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Binance API Configuration
                </CardTitle>
                <CardDescription>
                  Configure your Binance API credentials to distribute TON from your exchange account
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {binanceConfigured && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800 font-medium">
                      ✅ Binance credentials configured
                    </p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Binance API Key</label>
                  <Input
                    type="text"
                    placeholder="Enter your Binance API Key"
                    value={binanceApiKey}
                    onChange={(e) => setBinanceApiKey(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Binance API Secret</label>
                  <Input
                    type="password"
                    placeholder="Enter your Binance API Secret"
                    value={binanceApiSecret}
                    onChange={(e) => setBinanceApiSecret(e.target.value)}
                  />
                </div>
                
                <Button 
                  onClick={saveBinanceCredentials} 
                  disabled={loading || !binanceApiKey || !binanceApiSecret}
                  className="w-full"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Save Credentials
                </Button>
              </CardContent>
            </Card>

            {/* Binance Balance Card */}
            {binanceConfigured && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Binance TON Balance</CardTitle>
                    <Button size="sm" variant="outline" onClick={loadBinanceBalance} disabled={loading}>
                      <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {binanceBalance ? (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-600 font-medium">Available</p>
                        <p className="text-2xl font-bold text-blue-900">{binanceBalance.free.toFixed(2)} TON</p>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <p className="text-sm text-yellow-600 font-medium">Locked</p>
                        <p className="text-2xl font-bold text-yellow-900">{binanceBalance.locked.toFixed(2)} TON</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm text-green-600 font-medium">Total</p>
                        <p className="text-2xl font-bold text-green-900">{binanceBalance.total.toFixed(2)} TON</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500">Loading balance...</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Binance Internal Transfer Card - Перевод между пополнениями, спотом и фьючерсами */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowLeftRight className="h-5 w-5" />
                  Binance Internal Transfer
                </CardTitle>
                <CardDescription>
                  Move TON between Funding, Spot, and Futures wallets inside Binance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!binanceConfigured ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-yellow-800 font-medium mb-2">
                      ⚠️ Binance credentials not configured
                    </p>
                    <p className="text-xs text-yellow-700">
                      Please configure your Binance API credentials above to use internal transfers
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                      Use this to quickly shift liquidity between Funding (пополнения), Spot and USDT-M Futures без вывода на блокчейн.
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">From</label>
                        <select
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          value={binanceTransferFrom}
                          onChange={(e) => setBinanceTransferFrom(e.target.value)}
                        >
                          {binanceTransferAccounts.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">To</label>
                        <select
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          value={binanceTransferTo}
                          onChange={(e) => setBinanceTransferTo(e.target.value)}
                        >
                          {binanceTransferAccounts.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Asset</label>
                        <Input
                          value={binanceTransferAsset}
                          onChange={(e) => setBinanceTransferAsset(e.target.value.toUpperCase())}
                          placeholder="TON"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Amount</label>
                        <Input
                          type="number"
                          min="0"
                          step="0.00000001"
                          value={binanceTransferAmount}
                          onChange={(e) => setBinanceTransferAmount(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500">
                      Binance charges no withdrawal fees for internal transfers. Доступные направления: Spot ↔ Funding, Spot ↔ Futures, Funding ↔ Futures.
                    </p>
                    
                    <Button
                      onClick={handleBinanceInternalTransfer}
                      disabled={
                        binanceTransferLoading ||
                        binanceTransferFrom === binanceTransferTo ||
                        !binanceTransferAmount
                      }
                      className="w-full h-14"
                    >
                      {binanceTransferLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Processing transfer...
                        </>
                      ) : (
                        <>
                          <ArrowLeftRight className="h-5 w-5 mr-2" />
                          Transfer internally on Binance
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Binance Distribution Card */}
            {binanceConfigured && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5" />
                    Distribute TON from Binance
                  </CardTitle>
                  <CardDescription>
                    Send TON from your Binance account to managed wallets
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      ⚠️ Important: Binance has withdrawal limits and fees. Minimum delay between withdrawals is 10 seconds.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Recipient Wallets</label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={binanceRecipientFilter}
                      onChange={(e) => {
                        setBinanceRecipientFilter(e.target.value);
                        if (e.target.value !== 'groups') {
                          setBinanceSelectedGroups([]);
                        }
                      }}
                    >
                      <option value="selected">Selected Wallets ({selectedWallets.length})</option>
                      <option value="all">All Wallets ({wallets.length})</option>
                      <option value="zero_balance">Zero Balance Wallets ({wallets.filter(w => parseInt(w.ton_balance || 0) === 0).length})</option>
                      <option value="groups">Groups</option>
                    </select>
                    <p className="text-xs text-gray-500">
                      Select which wallets should receive TON from Binance
                    </p>
                  </div>

                  {binanceRecipientFilter === 'groups' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Select Groups</label>
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                        {getWalletGroups().map((group) => (
                          <label key={group.name} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={binanceSelectedGroups.includes(group.name)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBinanceSelectedGroups([...binanceSelectedGroups, group.name]);
                                } else {
                                  setBinanceSelectedGroups(binanceSelectedGroups.filter(g => g !== group.name));
                                }
                              }}
                              className="rounded"
                            />
                            <span className="text-sm">
                              {group.name} ({group.count} кошельков)
                            </span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        Selected: {binanceSelectedGroups.length} group(s), {wallets.filter(w => binanceSelectedGroups.includes(w.group_name || 'No Group')).length} wallets
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Amount Type</label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={binanceAmountType}
                      onChange={(e) => setBinanceAmountType(e.target.value)}
                    >
                      <option value="absolute">Absolute (TON)</option>
                      <option value="percentage">Percentage of Balance</option>
                    </select>
                  </div>

                  {binanceAmountType === 'absolute' ? (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Min Amount (TON)</label>
                        <Input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={binanceMinAmount}
                          onChange={(e) => setBinanceMinAmount(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Max Amount (TON)</label>
                        <Input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={binanceMaxAmount}
                          onChange={(e) => setBinanceMaxAmount(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Amount will be randomly selected between {binanceMinAmount.toFixed(2)} and {binanceMaxAmount.toFixed(2)} TON per wallet
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Min Percent (%)</label>
                        <Input
                          type="number"
                          min="0.1"
                          max="100"
                          step="0.1"
                          value={binanceMinPercent}
                          onChange={(e) => setBinanceMinPercent(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Max Percent (%)</label>
                        <Input
                          type="number"
                          min="0.1"
                          max="100"
                          step="0.1"
                          value={binanceMaxPercent}
                          onChange={(e) => setBinanceMaxPercent(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      {binanceBalance && (
                        <p className="text-xs text-gray-500">
                          Amount will be randomly selected between {(binanceBalance.free * binanceMinPercent / 100).toFixed(2)} and {(binanceBalance.free * binanceMaxPercent / 100).toFixed(2)} TON per wallet
                          ({(binanceBalance.free * binanceMinPercent / 100).toFixed(2)} - {(binanceBalance.free * binanceMaxPercent / 100).toFixed(2)}% of available balance)
                        </p>
                      )}
                    </div>
                  )}

                  {(() => {
                    let recipientCount = 0;
                    if (binanceRecipientFilter === 'selected') {
                      recipientCount = selectedWallets.length;
                    } else if (binanceRecipientFilter === 'all') {
                      recipientCount = wallets.length;
                    } else if (binanceRecipientFilter === 'zero_balance') {
                      recipientCount = wallets.filter(w => parseInt(w.ton_balance || 0) === 0).length;
                    } else if (binanceRecipientFilter === 'groups') {
                      recipientCount = wallets.filter(w => binanceSelectedGroups.includes(w.group_name || 'No Group')).length;
                    }
                    
                    let estimatedTotal = 0;
                    let estimatedText = '';
                    if (binanceAmountType === 'absolute' && recipientCount > 0) {
                      estimatedTotal = ((binanceMinAmount + binanceMaxAmount) / 2) * recipientCount;
                      estimatedText = `Estimated total required: ${estimatedTotal.toFixed(2)} TON for ${recipientCount} wallet(s)`;
                    } else if (binanceAmountType === 'percentage' && binanceBalance && recipientCount > 0) {
                      // For percentage mode, show info that amounts are calculated from remaining balance
                      const minAmount = (binanceBalance.free * binanceMinPercent / 100);
                      const maxAmount = (binanceBalance.free * binanceMaxPercent / 100);
                      estimatedText = `Amount will be randomly selected between ${minAmount.toFixed(2)} and ${maxAmount.toFixed(2)} TON per wallet (${binanceMinPercent}% - ${binanceMaxPercent}% of remaining balance)`;
                    }
                    
                    return estimatedText && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800">
                          {estimatedText}
                        </p>
                      </div>
                    );
                  })()}
                  
                  <Button 
                    onClick={handleBinanceDistribute} 
                    disabled={loading || !binanceConfigured || (binanceRecipientFilter === 'groups' && binanceSelectedGroups.length === 0)}
                    className="w-full h-16"
                  >
                    <Send className="h-5 w-5 mr-2" />
                    <div className="text-left">
                      <div className="font-bold">Distribute from Binance</div>
                      <div className="text-xs opacity-80">
                        {binanceRecipientFilter === 'selected' && `${selectedWallets.length} wallets selected`}
                        {binanceRecipientFilter === 'all' && `${wallets.length} wallets`}
                        {binanceRecipientFilter === 'zero_balance' && `${wallets.filter(w => parseInt(w.ton_balance || 0) === 0).length} zero-balance wallets`}
                        {binanceRecipientFilter === 'groups' && `${wallets.filter(w => binanceSelectedGroups.includes(w.group_name || 'No Group')).length} wallets from ${binanceSelectedGroups.length} group(s)`}
                      </div>
                    </div>
                  </Button>
                </CardContent>
              </Card>
            )}

            {!binanceConfigured && (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Settings className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600 mb-2">Binance not configured</p>
                    <p className="text-sm text-gray-500">Please enter your API credentials above to get started</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Import Mnemonic Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Import Mnemonic
                </CardTitle>
                <CardDescription>
                  Add mnemonic phrase to an existing wallet to enable transactions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Wallet Address</label>
                  <Input
                    placeholder="UQ..."
                    value={importAddress}
                    onChange={(e) => setImportAddress(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Select from your existing wallets
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Mnemonic (24 words)</label>
                  <textarea
                    className="w-full p-3 border rounded-md min-h-[100px] font-mono text-sm"
                    placeholder="word1 word2 word3 ... word24"
                    value={importMnemonic}
                    onChange={(e) => setImportMnemonic(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Paste your 24-word mnemonic phrase (separated by spaces)
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Security Warning:</strong> Never share your mnemonic with anyone. 
                    Make sure you trust this application before importing.
                  </p>
                </div>

                <Button
                  onClick={handleImportMnemonic}
                  disabled={importLoading || !importAddress || !importMnemonic}
                  className="w-full"
                >
                  {importLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Import Mnemonic
                    </>
                  )}
                </Button>

                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Why import?</strong></p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Enable sending TON from this wallet</li>
                    <li>Enable token trading operations</li>
                    <li>Required for all transaction signing</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Export/Backup Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Backup & Export
                </CardTitle>
                <CardDescription>
                  Import/Export all wallets and mnemonics for backup
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Import Section */}
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <h4 className="font-semibold text-green-900 mb-2">📥 Import Backup</h4>
                    <p className="text-sm text-green-800 mb-3">
                      Restore wallets from a previously exported JSON backup file.
                    </p>
                    <ul className="text-xs text-green-700 space-y-1 mb-3">
                      <li>• Imports wallet addresses and mnemonics</li>
                      <li>• Option to overwrite existing wallets</li>
                      <li>• Skips duplicates if overwrite is disabled</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={overwriteExisting}
                        onChange={(e) => setOverwriteExisting(e.target.checked)}
                        className="rounded"
                      />
                      <span>Overwrite existing wallets</span>
                    </label>

                    <input
                      type="file"
                      accept=".json"
                      onChange={handleFileSelect}
                      className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />

                    {importFileContent && (
                      <div className="bg-gray-50 p-3 rounded text-sm">
                        <p className="font-semibold">File loaded:</p>
                        <p className="text-gray-600">
                          {importFileContent.wallets?.length || 0} wallets ready to import
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Export date: {importFileContent.export_date}
                        </p>
                      </div>
                    )}

                    <Button
                      onClick={handleImportBackup}
                      disabled={importLoading || !importFileContent}
                      className="w-full"
                      variant="outline"
                    >
                      {importLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Import Backup File
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-4"></div>

                {/* Export Section */}
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📦 Export Backup</h4>
                    <p className="text-sm text-blue-800 mb-3">
                      Export all your wallets, addresses, balances, and mnemonics to a JSON file.
                    </p>
                    <ul className="text-xs text-blue-700 space-y-1 mb-3">
                      <li>• Includes all wallet addresses</li>
                      <li>• Includes mnemonic phrases (if available)</li>
                      <li>• Includes balances and metadata</li>
                      <li>• Safe offline storage format</li>
                    </ul>
                  </div>

                  <Button
                    onClick={handleExportWallets}
                    className="w-full"
                    variant="outline"
                  >
                    <Activity className="h-4 w-4 mr-2" />
                    Export All Wallets to JSON
                  </Button>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">
                    🔒 <strong>Security Notice:</strong> The exported file contains your mnemonics. 
                    Store it securely offline (USB drive, encrypted storage, etc.). 
                    Never upload it to cloud services or share it.
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Statistics</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-600 text-xs">Total Wallets</p>
                      <p className="font-bold text-lg">{wallets.length}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-600 text-xs">With Mnemonics</p>
                      <p className="font-bold text-lg">
                        {wallets.filter(w => w.mnemonic).length}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-600 text-xs">Without Mnemonics</p>
                      <p className="font-bold text-lg text-red-600">
                        {wallets.filter(w => !w.mnemonic).length}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-600 text-xs">Total Balance</p>
                      <p className="font-bold text-lg">
                        {(wallets.reduce((sum, w) => sum + parseInt(w.ton_balance || 0), 0) / 1e9).toFixed(2)} TON
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Mnemonic Dialog */}
      <Dialog open={showMnemonicDialog} onOpenChange={setShowMnemonicDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-red-600">⚠️ Wallet Mnemonic (Secret Phrase)</DialogTitle>
            <DialogDescription>
              Never share your mnemonic with anyone! Anyone with access to this phrase can steal your funds.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 font-medium">
                🔐 Security Warning: Write this down and store it in a safe place. Never store it digitally!
              </p>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-lg">
              <p className="text-xs text-gray-600 mb-2">Wallet Address:</p>
              <p 
                className="font-mono text-xs break-all cursor-pointer hover:bg-gray-200 p-2 rounded"
                onClick={() => copyToClipboard(selectedWalletForMnemonic)}
              >
                {selectedWalletForMnemonic}
              </p>
            </div>

            <div className="bg-white border-2 border-gray-300 p-6 rounded-lg">
              <p className="text-xs text-gray-600 mb-3">24-Word Mnemonic Phrase:</p>
              <div className="grid grid-cols-3 gap-3">
                {walletMnemonic.split(' ').map((word, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    <span className="text-xs text-gray-500 w-6">{index + 1}.</span>
                    <span className="font-mono text-sm font-medium">{word}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => copyToClipboard(walletMnemonic, 'Mnemonic')}
                className="flex-1"
                variant="outline"
              >
                Copy Mnemonic
              </Button>
              <Button
                onClick={() => {
                  setShowMnemonicDialog(false);
                  setWalletMnemonic('');
                }}
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Progress Log Dialog */}
      <Dialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Operation Progress
              {operationInProgress && (
                <RefreshCw className="h-4 w-4 animate-spin ml-2" />
              )}
            </DialogTitle>
            <DialogDescription>
              Real-time operation logs
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[500px] w-full rounded-md border p-4 bg-gray-900 text-gray-100 font-mono text-sm">
            {progressLogs.map((log, index) => (
              <div
                key={index}
                className={`mb-1 ${
                  log.type === 'error' ? 'text-red-400' :
                  log.type === 'success' ? 'text-green-400' :
                  log.type === 'warning' ? 'text-yellow-400' :
                  'text-gray-300'
                }`}
              >
                <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
              </div>
            ))}
            {progressLogs.length === 0 && (
              <div className="text-gray-500">Waiting for operation to start...</div>
            )}
          </ScrollArea>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                const logsText = progressLogs.map(log => `[${log.timestamp}] ${log.message}`).join('\n');
                navigator.clipboard.writeText(logsText);
                toast.success('Logs copied to clipboard');
              }}
            >
              Copy Logs
            </Button>
            <Button
              onClick={() => setShowProgressDialog(false)}
              disabled={operationInProgress}
            >
              {operationInProgress ? 'Please wait...' : 'Close'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WalletManager;
